import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useAuth } from '../../../app/auth';
import { Cliente } from '../../../types';
import {
  SorteoType,
  TicketItem,
  TicketItemData,
  MonazoTipo,
  PaymentMethod,
  SORTEO_CONFIG,
  adaptarItemParaSorteo,
  MESES,
  calcularTotalItem,
  createEmptyTicketsBySorteo,
  getSorteoModalidad,
  formatNumber2,
  TicketsBySorteo,
} from '../types';
import { Modal, Button } from '../../../components/ui';
import { Trash2, Pencil, X, CheckCircle, User, Send, Printer } from 'lucide-react';
import { creditoService } from '../../../services/creditoService';
import { AnularTicketModal } from '../components/AnularTicketModal';
import { ReimpresionTicketModal } from '../components/ReimpresionTicketModal';
import { fetchAPI } from '../../../services/apiClient';
import { procesarPostVenta, procesarReimpresion, TicketPayload, TicketJugada } from '../../../services/ticketService';
import { emitirVenta } from '../../../utils/listasBus';
import { TicketPreviewModal } from '../components/TicketPreviewModal';
import { getEstadoCierre, abrirCaja } from '../../caja/services/cajaVendedorService';
import { fetchRiesgoNumero } from '../../analitica/services/analiticaService';
import { fechaLocalYMD } from '../../../utils/date';

// ============================================
// API CONFIG & HELPERS
// ============================================

interface BackendSorteo {
  id: string;
  tipo_id: string;
  nombre: string;
  fecha: string;
  hora_apertura: string;
  hora_cierre: string;
  estado: string;
}

// Map frontend tab type → backend tipo_id
function tipoIdFromSorteoType(st: SorteoType): string {
  return st === 'TICA' ? 'NORMAL' : st;
}

// Map TicketItemData → jugadas for backend { num1, num2?, monto }
function itemsToJugadas(items: TicketItemData[]): Array<{ num1: string; num2?: string; monto: number }> {
  const jugadas: Array<{ num1: string; num2?: string; monto: number }> = [];
  for (const item of items) {
    switch (item.modalidad) {
      case 'NORMAL':
        jugadas.push({ num1: item.numero, monto: item.monto });
        break;
      case 'REVENTADO':
        if (item.montoRev1 > 0) jugadas.push({ num1: item.numero, num2: 'REV1', monto: item.montoRev1 });
        if (item.montoRev2 > 0) jugadas.push({ num1: item.numero, num2: 'REV2', monto: item.montoRev2 });
        break;
      case 'MEGA_REVENTADO':
        if (item.rev1 > 0) jugadas.push({ num1: item.numero, num2: 'REV1', monto: item.rev1 });
        if (item.rev2 > 0) jugadas.push({ num1: item.numero, num2: 'REV2', monto: item.rev2 });
        break;
      case 'MONAZOS':
        jugadas.push({ num1: `${item.d1}${item.d2}${item.d3}`, num2: item.tipo, monto: item.monto });
        break;
      case 'FECHAS':
        // Guardar fechas como dd-mm (número de mes) para que cuadre con la lista.
        {
          const monthMap: Record<string, number> = {
            'Enero': 1,
            'Febrero': 2,
            'Marzo': 3,
            'Abril': 4,
            'Mayo': 5,
            'Junio': 6,
            'Julio': 7,
            'Agosto': 8,
            'Septiembre': 9,
            'Octubre': 10,
            'Noviembre': 11,
            'Diciembre': 12,
          };
          const mm = (monthMap[item.mes] ?? 0).toString().padStart(2, '0');
          const dd = String(item.dia).padStart(2, '0');
          jugadas.push({ num1: `${dd}-${mm}`, monto: item.monto });
        }
        break;
      case 'PARLEY':
        jugadas.push({ num1: item.num1, num2: item.num2, monto: item.monto });
        break;
    }
  }
  return jugadas;
}

// ============================================
// SORTEO TABS COMPONENT
// ============================================
interface SorteoTabsProps {
  active: SorteoType;
  onChange: (sorteo: SorteoType) => void;
  ticketCounts: Record<SorteoType, number>;
}

const SorteoTabs: React.FC<SorteoTabsProps> = ({ active, onChange, ticketCounts }) => (
  <div className="flex flex-wrap gap-1 p-2 bg-gray-800 rounded-t-lg">
    {SORTEO_CONFIG.map(s => (
      <button
        key={s.id}
        onClick={() => onChange(s.id)}
        className={`
          px-3 py-2.5 text-sm font-bold rounded transition-all min-h-[44px]
          ${active === s.id
            ? `${s.color} text-white`
            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }
        `}
      >
        {s.label}
        {ticketCounts[s.id] > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-xs bg-white/20 rounded">
            {ticketCounts[s.id]}
          </span>
        )}
      </button>
    ))}
  </div>
);

// ============================================
// SORTEO COUNTDOWN COMPONENT
// ============================================
interface SorteoCountdownProps {
  horaCierre: string; // "HH:MM:SS" or "HH:MM"
  fecha: string;      // "YYYY-MM-DD"
  estado?: string;    // "ABIERTO" or "CERRADO"
  onExpired?: () => void;
}

const SorteoCountdown: React.FC<SorteoCountdownProps> = ({ horaCierre, fecha, estado = 'ABIERTO', onExpired }) => {
  const [remaining, setRemaining] = useState('');
  const [urgency, setUrgency] = useState<'normal' | 'warning' | 'critical'>('normal');
  const expiredFiredRef = useRef(false);

  useEffect(() => {
    expiredFiredRef.current = false;

    if (estado !== 'ABIERTO') return;

    const computeRemaining = () => {
      const [h, m, s] = horaCierre.split(':').map(Number);
      // Usar fecha de HOY para el cierre (sorteos recurrentes tienen fecha vieja)
      const now = new Date();
      const closeDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, s || 0, 0);
      const diffMs = closeDate.getTime() - now.getTime();

      if (diffMs <= 0) {
        setRemaining('CERRADO');
        setUrgency('critical');
        if (!expiredFiredRef.current) {
          expiredFiredRef.current = true;
          onExpired?.();
        }
        return;
      }

      const totalSec = Math.floor(diffMs / 1000);
      const hrs = Math.floor(totalSec / 3600);
      const mins = Math.floor((totalSec % 3600) / 60);
      const secs = totalSec % 60;

      const pad = (n: number) => n.toString().padStart(2, '0');
      const timeStr = hrs > 0
        ? `${pad(hrs)}:${pad(mins)}:${pad(secs)}`
        : `${pad(mins)}:${pad(secs)}`;
      setRemaining(`CIERRA EN ${timeStr}`);

      if (totalSec <= 60) setUrgency('critical');         // <= 1 min → rojo parpadeante
      else if (totalSec <= 300) setUrgency('warning');    // <= 5 min → amarillo
      else setUrgency('normal');                          // > 5 min → verde
    };

    computeRemaining();
    const timer = setInterval(computeRemaining, 1000);
    return () => clearInterval(timer);
  }, [horaCierre, fecha, estado, onExpired]);

  // Si backend dice CERRADO, mostrar badge
  if (estado !== 'ABIERTO' || remaining === 'CERRADO') {
    return (
      <div className="ml-auto flex items-center gap-1.5 font-mono text-sm font-bold text-red-400 animate-pulse">
        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
        <span>CERRADO</span>
      </div>
    );
  }

  const colorClasses = {
    normal: 'text-emerald-400',
    warning: 'text-yellow-400',
    critical: 'text-red-400 animate-pulse',
  };

  return (
    <div className={`ml-auto flex items-center gap-1.5 font-mono text-sm font-bold ${colorClasses[urgency]}`}>
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
      <span>{remaining}</span>
    </div>
  );
};

// ============================================
// CAPTURE FORM COMPONENT (por modalidad)
// ============================================
interface CaptureFormProps {
  sorteo: SorteoType;
  onAdd: (item: TicketItemData) => void;
  editingItem?: TicketItem | null;
  onCancelEdit?: () => void;
  onSaveEdit?: (item: TicketItemData) => void;
  resetAmountKey?: number;
  disabled?: boolean;
}

// Tipos de entrada para TICA
type TicaInputMode = 'SIMPLE' | 'RANGO' | 'INICIA' | 'TERMINA' | 'PARES';

const CaptureForm: React.FC<CaptureFormProps> = ({
  sorteo,
  onAdd,
  editingItem,
  onCancelEdit,
  onSaveEdit,
  resetAmountKey,
  disabled,
}) => {
  const modalidad = getSorteoModalidad(sorteo);
  const firstRef = useRef<HTMLInputElement>(null);
  const montoRef = useRef<HTMLInputElement>(null);
  const fechasMesRef = useRef<HTMLInputElement>(null);
  const parleyNum2Ref = useRef<HTMLInputElement>(null);
  const rangoHastaRef = useRef<HTMLInputElement>(null);
  const rev1Ref = useRef<HTMLInputElement>(null);
  const rev2Ref = useRef<HTMLInputElement>(null);
  const num2Ref = useRef<HTMLInputElement>(null);

  // Form states
  const [numero, setNumero] = useState('');
  const [monto, setMonto] = useState('0');
  const [rev1, setRev1] = useState('');
  const [rev2, setRev2] = useState('');
  const [num2, setNum2] = useState('');
  const [d1, setD1] = useState('');
  const [d2, setD2] = useState('');
  const [d3, setD3] = useState('');
  const [dia, setDia] = useState('');
  const [mes, setMes] = useState('');
  const [mesNum, setMesNum] = useState('');
  const [num1, setNum1] = useState('');

  // MONAZOS selector
  const [monazoTipos, setMonazoTipos] = useState<MonazoTipo[]>(['ORDEN']);

  // TICA specific states
  const [ticaMode, setTicaMode] = useState<TicaInputMode>('SIMPLE');
  const [rangoDesde, setRangoDesde] = useState('');
  const [rangoHasta, setRangoHasta] = useState('');
  const [digitoFiltro, setDigitoFiltro] = useState('');

  // Reset form
  const resetForm = useCallback((preserveMonto = false, preserveReventado = false) => {
    setNumero('');
    if (!preserveMonto) {
      setMonto('0');
    }
    if (!preserveReventado) {
      setRev1('');
      setRev2('');
    }
    setD1('');
    setD2('');
    setD3('');
    setDia('');
    setMes('');
    setMesNum('');
    setNum1('');
    setMonazoTipos(['ORDEN']);
    setRangoDesde('');
    setRangoHasta('');
    setDigitoFiltro('');
    setTimeout(() => firstRef.current?.focus(), 10);
  }, []);

  // Load editing data
  useEffect(() => {
    if (editingItem) {
      const data = editingItem.data;
      setTicaMode('SIMPLE'); // Always load as simple when editing
      switch (data.modalidad) {
        case 'NORMAL':
          setNumero(data.numero);
          setMonto(data.monto.toString());
          break;
        case 'REVENTADO':
          setNumero(data.numero);
          setRev1(data.montoRev1.toString());
          setRev2(data.montoRev2.toString());
          break;
        case 'MEGA_REVENTADO':
          setNumero(data.numero);
          setRev1(data.rev1.toString());
          setRev2(data.rev2.toString());
          break;
        case 'MONAZOS':
          setD1(data.d1);
          setD2(data.d2);
          setD3(data.d3);
          setMonto(data.monto.toString());
          setMonazoTipos([data.tipo]);
          break;
        case 'FECHAS':
          setDia(data.dia.toString());
          setMes(data.mes);
          {
            const idx = MESES.findIndex(m => m === data.mes);
            setMesNum(idx >= 0 ? String(idx + 1) : '');
          }
          setMonto(data.monto.toString());
          break;
        case 'PARLEY':
          setNum1(data.num1);
          setNumero(data.num2);
          setMonto(data.monto.toString());
          break;
      }
      setTimeout(() => firstRef.current?.focus(), 10);
    }
  }, [editingItem]);

  // Reset on sorteo change
  useEffect(() => {
    if (!editingItem) {
      resetForm(false);
      setTicaMode('SIMPLE');
    }
  }, [sorteo, editingItem, resetForm]);

  // MEGA UX: mostrar Rev2 como 0 por defecto
  useEffect(() => {
    if (editingItem) return;
    if (modalidad !== 'MEGA_REVENTADO') return;
    if (rev2 === '') setRev2('0');
  }, [modalidad, editingItem, rev2]);

  // Auto focus
  useEffect(() => {
    setTimeout(() => firstRef.current?.focus(), 50);
  }, [sorteo, ticaMode]);

  useEffect(() => {
    setMonto('0');
  }, [resetAmountKey]);

  // Generate numbers for TICA modes
  const generateTicaNumbers = (): string[] => {
    switch (ticaMode) {
      case 'SIMPLE':
        return numero ? [formatNumber2(numero)] : [];
      
      case 'RANGO': {
        if (!rangoDesde || !rangoHasta) return [];
        const desde = parseInt(rangoDesde);
        const hasta = parseInt(rangoHasta);
        if (isNaN(desde) || isNaN(hasta) || desde > hasta || desde < 0 || hasta > 99) return [];
        const nums: string[] = [];
        for (let i = desde; i <= hasta; i++) {
          nums.push(formatNumber2(i));
        }
        return nums;
      }
      
      case 'INICIA': {
        if (!digitoFiltro || digitoFiltro.length !== 1) return [];
        const d = parseInt(digitoFiltro);
        if (isNaN(d) || d < 0 || d > 9) return [];
        const nums: string[] = [];
        for (let i = 0; i <= 9; i++) {
          nums.push(formatNumber2(d * 10 + i)); // 90, 91, 92...99
        }
        return nums;
      }
      
      case 'TERMINA': {
        if (!digitoFiltro || digitoFiltro.length !== 1) return [];
        const d = parseInt(digitoFiltro);
        if (isNaN(d) || d < 0 || d > 9) return [];
        const nums: string[] = [];
        for (let i = 0; i <= 9; i++) {
          nums.push(formatNumber2(i * 10 + d)); // 03, 13, 23...93
        }
        return nums;
      }
      
      case 'PARES': {
        // 00, 11, 22, 33, 44, 55, 66, 77, 88, 99
        return ['00', '11', '22', '33', '44', '55', '66', '77', '88', '99'];
      }
      
      default:
        return [];
    }
  };

  // Handle submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;

    const requirePositive = (value: string, focusRef?: React.RefObject<HTMLInputElement | null>) => {
      const n = parseInt(value, 10) || 0;
      if (n <= 0) {
        const el = focusRef?.current;
        if (el) {
          setTimeout(() => {
            el.focus();
            try { el.select(); } catch {}
          }, 0);
        }
        return null;
      }
      return n;
    };

    // Handle TICA with multiple numbers
    if (modalidad === 'NORMAL' && !editingItem) {
      const numbers = generateTicaNumbers();
      if (numbers.length === 0) return;

      const montoVal = requirePositive(monto, montoRef);
      if (montoVal === null) return;

      numbers.forEach(num => {
        onAdd({ modalidad: 'NORMAL', numero: num, monto: montoVal });
      });
      resetForm(true);
      return;
    }

    // Handle TICA with multiple numbers (REVENTADO)
    if (modalidad === 'REVENTADO' && !editingItem) {
      const numbers = generateTicaNumbers();
      if (numbers.length === 0) return;

      const rev1Val = requirePositive(rev1, rev1Ref);
      if (rev1Val === null) return;
      // En REVENTADO, Rev2 puede ser 0 (opcional). Si está vacío, se toma como 0.
      let rev2Val = Math.max(0, parseInt(rev2 || '0', 10) || 0);
      // Rev2 no puede ser mayor a Rev1
      if (rev2Val > rev1Val) rev2Val = rev1Val;

      numbers.forEach(num => {
        onAdd({ modalidad: 'REVENTADO', numero: num, montoRev1: rev1Val, montoRev2: rev2Val });
      });
      // Mantener Rev1/Rev2 para capturas rápidas
      resetForm(true, true);
      return;
    }

    // Handle TICA with multiple numbers (MEGA)
    if (modalidad === 'MEGA_REVENTADO' && !editingItem) {
      const numbers = generateTicaNumbers();
      if (numbers.length === 0) return;

      const rev1Val = requirePositive(rev1, rev1Ref);
      if (rev1Val === null) return;
      let rev2Val = Math.max(0, parseInt(rev2 || '0', 10) || 0);
      // Rev2 no puede ser mayor a Rev1
      if (rev2Val > rev1Val) rev2Val = rev1Val;

      numbers.forEach(num => {
        onAdd({ modalidad: 'MEGA_REVENTADO', numero: num, rev1: rev1Val, rev2: rev2Val });
      });

      // Mantener Rev1/Rev2 para capturas rápidas
      resetForm(true, true);
      return;
    }

    let item: TicketItemData | null = null;

    switch (modalidad) {
      case 'NORMAL':
        if (!numero) return;
        {
          const montoVal = requirePositive(monto, montoRef);
          if (montoVal === null) return;
          item = { modalidad: 'NORMAL', numero: formatNumber2(numero), monto: montoVal };
        }
        break;
      case 'REVENTADO':
        if (!numero) return;
        {
          const rev1Val = requirePositive(rev1, rev1Ref);
          if (rev1Val === null) return;
          // En REVENTADO, Rev2 puede ser 0 (opcional). Si está vacío, se toma como 0.
          let rev2Val = Math.max(0, parseInt(rev2 || '0', 10) || 0);
          // Rev2 no puede ser mayor a Rev1
          if (rev2Val > rev1Val) rev2Val = rev1Val;
          item = { modalidad: 'REVENTADO', numero: formatNumber2(numero), montoRev1: rev1Val, montoRev2: rev2Val };
        }
        break;
      case 'MEGA_REVENTADO':
        if (!numero) return;
        {
          const rev1Val = requirePositive(rev1, rev1Ref);
          if (rev1Val === null) return;
          let rev2Val = Math.max(0, parseInt(rev2 || '0', 10) || 0);
          // Rev2 no puede ser mayor a Rev1
          if (rev2Val > rev1Val) rev2Val = rev1Val;
          item = { modalidad: 'MEGA_REVENTADO', numero: formatNumber2(numero), rev1: rev1Val, rev2: rev2Val };
        }
        break;
      case 'MONAZOS':
        if (!d1 || !d2 || !d3) return;
        {
          const montoVal = requirePositive(monto, montoRef);
          if (montoVal === null) return;
          const tipos = (editingItem ? [monazoTipos[0] ?? 'ORDEN'] : (monazoTipos.length ? monazoTipos : ['ORDEN'])) as MonazoTipo[];
          if (!editingItem) {
            tipos.forEach(tipo => {
              onAdd({ modalidad: 'MONAZOS', d1, d2, d3, monto: montoVal, tipo });
            });
            resetForm(true);
            return;
          }
          item = { modalidad: 'MONAZOS', d1, d2, d3, monto: montoVal, tipo: tipos[0] };
        }
        break;
      case 'FECHAS':
        if (!dia) return;
        {
          if (!mesNum || !mes) {
            setTimeout(() => fechasMesRef.current?.focus(), 0);
            return;
          }
          const montoVal = requirePositive(monto, montoRef);
          if (montoVal === null) return;
          item = { modalidad: 'FECHAS', dia: parseInt(dia) || 1, mes, monto: montoVal };
        }
        break;
      case 'PARLEY':
        if (!num1 || !numero) return;
        {
          const montoVal = requirePositive(monto, montoRef);
          if (montoVal === null) return;
          item = { modalidad: 'PARLEY', num1: formatNumber2(num1), num2: formatNumber2(numero), monto: montoVal };
        }
        break;
    }

    if (item) {
      if (editingItem && onSaveEdit) {
        onSaveEdit(item);
        resetForm(false);
      } else {
        onAdd(item);
        // FECHAS: mantener monto (y mes) para capturas rápidas
        if (modalidad === 'FECHAS') {
          setDia('');
          setMes('');
          setMesNum('');
          setTimeout(() => firstRef.current?.focus(), 10);
          return;
        }
        resetForm(true, modalidad === 'REVENTADO');
      }
    }
  };

  // Input styles
  const inputCls = "w-full px-3 py-3 text-xl font-mono font-bold text-center border-2 border-gray-600 bg-gray-900 text-white rounded focus:border-yellow-400 focus:outline-none";
  const labelCls = "block text-xs text-gray-400 mb-1 uppercase tracking-wide";
  const smallInputCls = "w-16 px-2 py-3 text-xl font-mono font-bold text-center border-2 border-gray-600 bg-gray-900 text-white rounded focus:border-yellow-400 focus:outline-none";
  const tabCls = (active: boolean) => `flex-1 py-1 px-1 text-[10px] font-bold rounded transition-all ${active ? 'bg-yellow-500 text-black' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`;

  const megaSecondEnabled = false; // segundo número eliminado: MEGA usa solo Rev1/Rev2

  // Focus next on maxLength
  const handleNumeroChange = (val: string, maxLen: number, nextRef?: React.RefObject<HTMLInputElement | null>) => {
    const clean = val.replace(/\D/g, '').slice(0, maxLen);
    if (clean.length === maxLen && nextRef?.current) {
      setTimeout(() => nextRef.current?.focus(), 10);
    }
    return clean;
  };

  // Preview numbers for TICA
  const previewNumbers = (modalidad === 'NORMAL' || modalidad === 'REVENTADO' || modalidad === 'MEGA_REVENTADO') && !editingItem
    ? generateTicaNumbers()
    : [];

  // Render form by modalidad
  const renderForm = () => {
    switch (modalidad) {
      case 'NORMAL':
        return (
          <>
            {/* Mode selector - only when not editing */}
            {!editingItem && (
              <div className="flex gap-1 mb-3">
                <button type="button" onClick={() => setTicaMode('SIMPLE')} className={tabCls(ticaMode === 'SIMPLE')}>Simple</button>
                <button type="button" onClick={() => setTicaMode('RANGO')} className={tabCls(ticaMode === 'RANGO')}>Rango</button>
                <button type="button" onClick={() => setTicaMode('INICIA')} className={tabCls(ticaMode === 'INICIA')}>Inicia</button>
                <button type="button" onClick={() => setTicaMode('TERMINA')} className={tabCls(ticaMode === 'TERMINA')}>Term.</button>
                <button type="button" onClick={() => setTicaMode('PARES')} className={tabCls(ticaMode === 'PARES')}>Pares</button>
              </div>
            )}

            {/* Simple mode */}
            {(ticaMode === 'SIMPLE' || editingItem) && (
              <div>
                <label className={labelCls}>Número</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={numero}
                  onChange={e => setNumero(handleNumeroChange(e.target.value, 2, montoRef))}
                  placeholder="00"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
            )}

            {/* Range mode */}
            {ticaMode === 'RANGO' && !editingItem && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Desde</label>
                  <input
                    ref={firstRef}
                    type="text"
                    inputMode="numeric"
                    value={rangoDesde}
                    onChange={e => setRangoDesde(handleNumeroChange(e.target.value, 2, rangoHastaRef))}
                    placeholder="00"
                    className={inputCls}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className={labelCls}>Hasta</label>
                  <input
                    ref={rangoHastaRef}
                    type="text"
                    inputMode="numeric"
                    value={rangoHasta}
                    onChange={e => setRangoHasta(handleNumeroChange(e.target.value, 2, montoRef))}
                    placeholder="99"
                    className={inputCls}
                    autoComplete="off"
                  />
                </div>
              </div>
            )}

            {/* Inicia con mode */}
            {ticaMode === 'INICIA' && !editingItem && (
              <div>
                <label className={labelCls}>Inicia con (0-9)</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={digitoFiltro}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setDigitoFiltro(v);
                    if (v) setTimeout(() => montoRef.current?.focus(), 10);
                  }}
                  placeholder="9"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  {digitoFiltro ? `→ ${digitoFiltro}0, ${digitoFiltro}1, ${digitoFiltro}2...${digitoFiltro}9` : 'Ej: 9 → 90,91,92...99'}
                </p>
              </div>
            )}

            {/* Termina con mode */}
            {ticaMode === 'TERMINA' && !editingItem && (
              <div>
                <label className={labelCls}>Termina con (0-9)</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={digitoFiltro}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setDigitoFiltro(v);
                    if (v) setTimeout(() => montoRef.current?.focus(), 10);
                  }}
                  placeholder="3"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  {digitoFiltro ? `→ 0${digitoFiltro}, 1${digitoFiltro}, 2${digitoFiltro}...9${digitoFiltro}` : 'Ej: 3 → 03,13,23...93'}
                </p>
              </div>
            )}

            {/* Pares mode - no input needed, just shows preview */}
            {ticaMode === 'PARES' && !editingItem && (
              <div className="bg-gray-800 rounded p-2">
                <p className="text-xs text-gray-400 mb-1">10 números (pares):</p>
                <p className="text-xs text-yellow-400 font-mono">
                  00, 11, 22, 33, 44, 55, 66, 77, 88, 99
                </p>
              </div>
            )}

            {/* Preview of numbers to add */}
            {previewNumbers.length > 1 && ticaMode !== 'PARES' && (
              <div className="bg-gray-800 rounded p-2 mt-2">
                <p className="text-xs text-gray-400 mb-1">{previewNumbers.length} números:</p>
                <p className="text-xs text-yellow-400 font-mono break-all">
                  {previewNumbers.join(', ')}
                </p>
              </div>
            )}

            <div>
              <label className={labelCls}>Monto ₡</label>
              <input
                ref={montoRef}
                type="text"
                inputMode="numeric"
                value={monto}
                onChange={e => setMonto(e.target.value.replace(/\D/g, ''))}
                onFocus={e => e.target.select()}
                placeholder="100"
                className={inputCls}
                autoComplete="off"
              />
            </div>
          </>
        );

      case 'REVENTADO':
        return (
          <>
            {/* Mode selector - only when not editing */}
            {!editingItem && (
              <div className="flex gap-1 mb-3">
                <button type="button" onClick={() => setTicaMode('SIMPLE')} className={tabCls(ticaMode === 'SIMPLE')}>Simple</button>
                <button type="button" onClick={() => setTicaMode('RANGO')} className={tabCls(ticaMode === 'RANGO')}>Rango</button>
                <button type="button" onClick={() => setTicaMode('INICIA')} className={tabCls(ticaMode === 'INICIA')}>Inicia</button>
                <button type="button" onClick={() => setTicaMode('TERMINA')} className={tabCls(ticaMode === 'TERMINA')}>Term.</button>
                <button type="button" onClick={() => setTicaMode('PARES')} className={tabCls(ticaMode === 'PARES')}>Pares</button>
              </div>
            )}

            {/* Simple mode */}
            {(ticaMode === 'SIMPLE' || editingItem) && (
              <div>
                <label className={labelCls}>Número</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={numero}
                  onChange={e => setNumero(handleNumeroChange(e.target.value, 2, rev1Ref))}
                  placeholder="00"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
            )}

            {/* Range mode */}
            {ticaMode === 'RANGO' && !editingItem && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Desde</label>
                  <input
                    ref={firstRef}
                    type="text"
                    inputMode="numeric"
                    value={rangoDesde}
                    onChange={e => setRangoDesde(handleNumeroChange(e.target.value, 2, rangoHastaRef))}
                    placeholder="00"
                    className={inputCls}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className={labelCls}>Hasta</label>
                  <input
                    ref={rangoHastaRef}
                    type="text"
                    inputMode="numeric"
                    value={rangoHasta}
                    onChange={e => setRangoHasta(handleNumeroChange(e.target.value, 2, rev1Ref))}
                    placeholder="99"
                    className={inputCls}
                    autoComplete="off"
                  />
                </div>
              </div>
            )}

            {/* Inicia con mode */}
            {ticaMode === 'INICIA' && !editingItem && (
              <div>
                <label className={labelCls}>Inicia con (0-9)</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={digitoFiltro}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setDigitoFiltro(v);
                    if (v) setTimeout(() => rev1Ref.current?.focus(), 10);
                  }}
                  placeholder="9"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  {digitoFiltro ? `→ ${digitoFiltro}0, ${digitoFiltro}1, ${digitoFiltro}2...${digitoFiltro}9` : 'Ej: 9 → 90,91,92...99'}
                </p>
              </div>
            )}

            {/* Termina con mode */}
            {ticaMode === 'TERMINA' && !editingItem && (
              <div>
                <label className={labelCls}>Termina con (0-9)</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={digitoFiltro}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setDigitoFiltro(v);
                    if (v) setTimeout(() => rev1Ref.current?.focus(), 10);
                  }}
                  placeholder="3"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  {digitoFiltro ? `→ 0${digitoFiltro}, 1${digitoFiltro}, 2${digitoFiltro}...9${digitoFiltro}` : 'Ej: 3 → 03,13,23...93'}
                </p>
              </div>
            )}

            {/* Pares mode - no input needed, just shows preview */}
            {ticaMode === 'PARES' && !editingItem && (
              <div className="bg-gray-800 rounded p-2">
                <p className="text-xs text-gray-400 mb-1">10 números (pares):</p>
                <p className="text-xs text-yellow-400 font-mono">
                  00, 11, 22, 33, 44, 55, 66, 77, 88, 99
                </p>
              </div>
            )}

            {/* Preview of numbers to add */}
            {previewNumbers.length > 1 && ticaMode !== 'PARES' && (
              <div className="bg-gray-800 rounded p-2 mt-2">
                <p className="text-xs text-gray-400 mb-1">{previewNumbers.length} números:</p>
                <p className="text-xs text-yellow-400 font-mono break-all">
                  {previewNumbers.join(', ')}
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Rev1 ₡</label>
                <input
                  ref={rev1Ref}
                  type="text"
                  inputMode="numeric"
                  value={rev1}
                  onChange={e => setRev1(e.target.value.replace(/\D/g, ''))}
                  onFocus={e => e.target.select()}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      montoRef.current?.focus();
                    }
                  }}
                  placeholder="100"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className={labelCls}>REV2 ₡</label>
                <input
                  ref={montoRef}
                  type="text"
                  inputMode="numeric"
                  value={rev2}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '');
                    const rev1Val = parseInt(rev1 || '0', 10) || 0;
                    const rev2Val = parseInt(val || '0', 10) || 0;
                    if (rev1Val > 0 && rev2Val > rev1Val) {
                      setRev2(rev1Val.toString());
                    } else {
                      setRev2(val);
                    }
                  }}
                  onFocus={e => e.target.select()}
                  placeholder="100"
                  disabled={!(parseInt(rev1 || '0', 10) || 0)}
                  className={`${inputCls} ${(() => { const r1 = parseInt(rev1||'0',10)||0; const r2 = parseInt(rev2||'0',10)||0; return r2 > 0 && r1 > 0 && r2 > r1 ? 'ring-2 ring-red-500' : ''; })() || !(parseInt(rev1||'0',10)||0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  autoComplete="off"
                />
              </div>
            </div>
          </>
        );

      case 'MEGA_REVENTADO':
        return (
          <>
            {/* Mode selector - only when not editing */}
            {!editingItem && (
              <div className="flex gap-1 mb-3">
                <button type="button" onClick={() => setTicaMode('SIMPLE')} className={tabCls(ticaMode === 'SIMPLE')}>Simple</button>
                <button type="button" onClick={() => setTicaMode('RANGO')} className={tabCls(ticaMode === 'RANGO')}>Rango</button>
                <button type="button" onClick={() => setTicaMode('INICIA')} className={tabCls(ticaMode === 'INICIA')}>Inicia</button>
                <button type="button" onClick={() => setTicaMode('TERMINA')} className={tabCls(ticaMode === 'TERMINA')}>Term.</button>
                <button type="button" onClick={() => setTicaMode('PARES')} className={tabCls(ticaMode === 'PARES')}>Pares</button>
              </div>
            )}

            {/* Simple mode */}
            {(ticaMode === 'SIMPLE' || editingItem) && (
              <div>
                <label className={labelCls}>Número</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={numero}
                  onChange={e => setNumero(handleNumeroChange(e.target.value, 2, rev1Ref))}
                  placeholder="00"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
            )}

            {/* Range mode */}
            {ticaMode === 'RANGO' && !editingItem && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className={labelCls}>Desde</label>
                  <input
                    ref={firstRef}
                    type="text"
                    inputMode="numeric"
                    value={rangoDesde}
                    onChange={e => setRangoDesde(handleNumeroChange(e.target.value, 2, rangoHastaRef))}
                    placeholder="00"
                    className={inputCls}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className={labelCls}>Hasta</label>
                  <input
                    ref={rangoHastaRef}
                    type="text"
                    inputMode="numeric"
                    value={rangoHasta}
                    onChange={e => setRangoHasta(handleNumeroChange(e.target.value, 2, rev1Ref))}
                    placeholder="99"
                    className={inputCls}
                    autoComplete="off"
                  />
                </div>
              </div>
            )}

            {/* Inicia con mode */}
            {ticaMode === 'INICIA' && !editingItem && (
              <div>
                <label className={labelCls}>Inicia con (0-9)</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={digitoFiltro}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setDigitoFiltro(v);
                    if (v) setTimeout(() => rev1Ref.current?.focus(), 10);
                  }}
                  placeholder="9"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  {digitoFiltro ? `→ ${digitoFiltro}0, ${digitoFiltro}1, ${digitoFiltro}2...${digitoFiltro}9` : 'Ej: 9 → 90,91,92...99'}
                </p>
              </div>
            )}

            {/* Termina con mode */}
            {ticaMode === 'TERMINA' && !editingItem && (
              <div>
                <label className={labelCls}>Termina con (0-9)</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={digitoFiltro}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setDigitoFiltro(v);
                    if (v) setTimeout(() => rev1Ref.current?.focus(), 10);
                  }}
                  placeholder="3"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  {digitoFiltro ? `→ 0${digitoFiltro}, 1${digitoFiltro}, 2${digitoFiltro}...9${digitoFiltro}` : 'Ej: 3 → 03,13,23...93'}
                </p>
              </div>
            )}

            {/* Pares mode - no input needed, just shows preview */}
            {ticaMode === 'PARES' && !editingItem && (
              <div className="bg-gray-800 rounded p-2">
                <p className="text-xs text-gray-400 mb-1">10 números (pares):</p>
                <p className="text-xs text-yellow-400 font-mono">
                  00, 11, 22, 33, 44, 55, 66, 77, 88, 99
                </p>
              </div>
            )}

            {/* Preview of numbers to add */}
            {previewNumbers.length > 1 && ticaMode !== 'PARES' && (
              <div className="bg-gray-800 rounded p-2 mt-2">
                <p className="text-xs text-gray-400 mb-1">{previewNumbers.length} números:</p>
                <p className="text-xs text-yellow-400 font-mono break-all">
                  {previewNumbers.join(', ')}
                </p>
              </div>
            )}

            {/* Rev1 → Rev2 */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Rev1 ₡</label>
                <input
                  ref={rev1Ref}
                  type="text"
                  inputMode="numeric"
                  value={rev1}
                  onChange={e => setRev1(e.target.value.replace(/\D/g, ''))}
                  onFocus={e => e.target.select()}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      rev2Ref.current?.focus();
                    }
                  }}
                  placeholder="100"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className={labelCls}>Rev2 ₡</label>
                <input
                  ref={rev2Ref}
                  type="text"
                  inputMode="numeric"
                  value={rev2}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '');
                    const rev1Val = parseInt(rev1 || '0', 10) || 0;
                    const rev2Val = parseInt(val || '0', 10) || 0;
                    if (rev1Val > 0 && rev2Val > rev1Val) {
                      setRev2(rev1Val.toString());
                    } else {
                      setRev2(val);
                    }
                  }}
                  onFocus={e => e.target.select()}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.currentTarget.form?.requestSubmit();
                    }
                  }}
                  placeholder="100"
                  disabled={!(parseInt(rev1 || '0', 10) || 0)}
                  className={`${inputCls} ${!(parseInt(rev1 || '0', 10) || 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  autoComplete="off"
                />
              </div>
            </div>
          </>
        );

      case 'MONAZOS':
        return (
          <>
            <div>
              <label className={labelCls}>Tipo de jugada</label>
              <div className="flex gap-1">
                {([
                  { id: 'ORDEN', label: 'Orden' },
                  { id: 'DESORDEN', label: 'Desorden' },
                  { id: 'P2', label: 'P2' },
                  { id: 'U2', label: 'U2' },
                ] as const).map(opt => {
                  const active = monazoTipos.includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className={tabCls(active)}
                      onClick={() => {
                        setMonazoTipos(prev => {
                          const next = prev.includes(opt.id)
                            ? prev.filter(t => t !== opt.id)
                            : [...prev, opt.id];
                          // Ensure at least one option remains selected
                          if (next.length === 0) return ['ORDEN'];
                          // While editing, keep single selection
                          if (editingItem) return [opt.id];
                          return next;
                        });
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {!editingItem && monazoTipos.length > 1 && (
                <p className="text-xs text-gray-500 mt-1">Se agregará 1 jugada por cada tipo seleccionado (mismo monto y números).</p>
              )}
            </div>
            <div>
              <label className={labelCls}>Dígitos</label>
              <div className="flex items-center justify-center gap-2">
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d1}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setD1(v);
                    if (v) document.getElementById('d2-input')?.focus();
                  }}
                  placeholder="0"
                  className={smallInputCls}
                  autoComplete="off"
                />
                <span className="text-2xl text-gray-500">-</span>
                <input
                  id="d2-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d2}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setD2(v);
                    if (v) document.getElementById('d3-input')?.focus();
                  }}
                  placeholder="0"
                  className={smallInputCls}
                  autoComplete="off"
                />
                <span className="text-2xl text-gray-500">-</span>
                <input
                  id="d3-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d3}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 1);
                    setD3(v);
                    if (v) montoRef.current?.focus();
                  }}
                  placeholder="0"
                  className={smallInputCls}
                  autoComplete="off"
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>Monto ₡</label>
              <input
                ref={montoRef}
                type="text"
                inputMode="numeric"
                value={monto}
                onChange={e => setMonto(e.target.value.replace(/\D/g, ''))}
                onFocus={e => e.target.select()}
                placeholder="100"
                className={inputCls}
                autoComplete="off"
              />
            </div>
          </>
        );

      case 'FECHAS':
        return (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Día</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={dia}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '');
                    const n = parseInt(v);
                    if (v === '' || (n >= 1 && n <= 31)) {
                      setDia(v);
                      if (v.length === 2) setTimeout(() => fechasMesRef.current?.focus(), 10);
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      fechasMesRef.current?.focus();
                    }
                  }}
                  placeholder="1"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className={labelCls}>Mes (1-12)</label>
                <input
                  ref={fechasMesRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={mesNum}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 2);
                    if (v === '') {
                      setMesNum('');
                      setMes('');
                      return;
                    }
                    const n = parseInt(v, 10);
                    if (isNaN(n) || n < 1 || n > 12) return;
                    setMesNum(v);
                    setMes(MESES[n - 1]);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      montoRef.current?.focus();
                      setTimeout(() => {
                        try { montoRef.current?.select(); } catch {}
                      }, 0);
                    }
                  }}
                  placeholder="2"
                  className={inputCls}
                  autoComplete="off"
                />
                <p className="text-[10px] text-gray-500 mt-1 text-center">
                  {(() => {
                    const n = parseInt(mesNum || '', 10);
                    if (!isNaN(n) && n >= 1 && n <= 12) return MESES[n - 1];
                    return mes || '\u00A0';
                  })()}
                </p>
              </div>
            </div>
            <div>
              <label className={labelCls}>Monto ₡</label>
              <input
                ref={montoRef}
                type="text"
                inputMode="numeric"
                value={monto}
                onChange={e => setMonto(e.target.value.replace(/\D/g, ''))}
                onFocus={e => e.target.select()}
                placeholder="100"
                className={inputCls}
                autoComplete="off"
              />
            </div>
          </>
        );

      case 'PARLEY':
        return (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Núm 1</label>
                <input
                  ref={firstRef}
                  type="text"
                  inputMode="numeric"
                  value={num1}
                  onChange={e => {
                    const clean = handleNumeroChange(e.target.value, 2, parleyNum2Ref);
                    setNum1(clean);
                    if (clean.length === 2) {
                      setTimeout(() => {
                        try { parleyNum2Ref.current?.select(); } catch {}
                      }, 20);
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      parleyNum2Ref.current?.focus();
                      setTimeout(() => {
                        try { parleyNum2Ref.current?.select(); } catch {}
                      }, 0);
                    }
                  }}
                  onFocus={e => e.target.select()}
                  placeholder="00"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className={labelCls}>Núm 2</label>
                <input
                  ref={parleyNum2Ref}
                  type="text"
                  inputMode="numeric"
                  value={numero}
                  onChange={e => {
                    const clean = handleNumeroChange(e.target.value, 2, montoRef);
                    setNumero(clean);
                    if (clean.length === 2) {
                      setTimeout(() => {
                        try { montoRef.current?.select(); } catch {}
                      }, 20);
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      montoRef.current?.focus();
                      setTimeout(() => {
                        try { montoRef.current?.select(); } catch {}
                      }, 0);
                    }
                  }}
                  onFocus={e => e.target.select()}
                  placeholder="00"
                  className={inputCls}
                  autoComplete="off"
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>Monto ₡</label>
              <input
                ref={montoRef}
                type="text"
                inputMode="numeric"
                value={monto}
                onChange={e => setMonto(e.target.value.replace(/\D/g, ''))}
                onFocus={e => e.target.select()}
                placeholder="100"
                className={inputCls}
                autoComplete="off"
              />
            </div>
          </>
        );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {renderForm()}
      <div className="flex gap-2 pt-2">
        {editingItem ? (
          <>
            <button
              type="button"
              onClick={onCancelEdit}
              className="flex-1 py-3 bg-gray-700 text-white font-bold rounded hover:bg-gray-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 py-3 bg-yellow-500 text-black font-bold rounded hover:bg-yellow-400"
            >
              Guardar
            </button>
          </>
        ) : (
          <button
            type="submit"
            disabled={disabled}
            className={`w-full py-3 font-bold rounded text-lg ${
              disabled
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-500'
            }`}
          >
            + Agregar
          </button>
        )}
      </div>
    </form>
  );
};

// ============================================
// TICKET PANEL COMPONENT
// ============================================
interface TicketPanelProps {
  sorteo: SorteoType;
  items: TicketItem[];
  onEdit: (item: TicketItem) => void;
  onRemove: (id: string) => void;
  onClear: () => void;
  onViewList?: () => void;
  showViewListButton?: boolean;
}

const TicketPanel: React.FC<TicketPanelProps> = ({
  sorteo,
  items,
  onEdit,
  onRemove,
  onClear,
  onViewList,
  showViewListButton = true,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const total = items.reduce((sum, item) => sum + calcularTotalItem(item.data), 0);
  const sorteoConfig = SORTEO_CONFIG.find(s => s.id === sorteo)!;
  const modalidad = getSorteoModalidad(sorteo);

  const displayItems = (() => {
    // Orden visual por modalidad (sin cambiar el orden real de anotación)
    // REVENTADO: primero Rev1 solo (Rev2=0), luego los que llevan Rev2.
    if (modalidad === 'REVENTADO') {
      return items
        .map((it, idx) => ({ it, idx }))
        .sort((a, b) => {
          const aHasRev2 = a.it.data.modalidad === 'REVENTADO' && (a.it.data.montoRev2 ?? 0) > 0;
          const bHasRev2 = b.it.data.modalidad === 'REVENTADO' && (b.it.data.montoRev2 ?? 0) > 0;
          const aGroup = aHasRev2 ? 1 : 0;
          const bGroup = bHasRev2 ? 1 : 0;
          if (aGroup !== bGroup) return aGroup - bGroup;
          return a.idx - b.idx;
        })
        .map(x => x.it);
    }

    // MEGA: primero solo Rev1 (Rev2=0), luego Rev1+Rev2 (sin Num2), y de último los que llevan todo.
    if (modalidad === 'MEGA_REVENTADO') {
      return items
        .map((it, idx) => ({ it, idx }))
        .sort((a, b) => {
          const aData = a.it.data;
          const bData = b.it.data;

          const aRev2 = aData.modalidad === 'MEGA_REVENTADO' ? (aData.rev2 ?? 0) : 0;
          const bRev2 = bData.modalidad === 'MEGA_REVENTADO' ? (bData.rev2 ?? 0) : 0;
          const aHasNum2 = false;
          const bHasNum2 = false;

          const aGroup = aRev2 <= 0 ? 0 : (aHasNum2 ? 2 : 1);
          const bGroup = bRev2 <= 0 ? 0 : (bHasNum2 ? 2 : 1);
          if (aGroup !== bGroup) return aGroup - bGroup;
          return a.idx - b.idx;
        })
        .map(x => x.it);
    }

    return items;
  })();

  // Auto scroll to bottom
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [items.length]);

  type TicketColumn = { key: string; label: string; value: string; align?: 'left' | 'right' | 'center' };

  const getColumns = (data: TicketItemData): TicketColumn[] => {
    switch (data.modalidad) {
      case 'NORMAL':
        return [
          { key: 'numero', label: 'Número', value: `#${data.numero}`, align: 'center' },
          { key: 'monto', label: 'Monto', value: `₡${data.monto}`, align: 'right' },
        ];
      case 'REVENTADO':
        return [
          { key: 'numero', label: 'Número', value: `#${data.numero}`, align: 'center' },
          { key: 'rev1', label: 'Rev1', value: `₡${data.montoRev1}`, align: 'right' },
          { key: 'rev2', label: 'Rev2', value: data.montoRev2 > 0 ? `₡${data.montoRev2}` : '\u00A0', align: 'right' },
        ];
      case 'MEGA_REVENTADO':
        return [
          { key: 'n1', label: 'Número', value: `#${data.numero}`, align: 'center' },
          { key: 'rev1', label: 'Rev1', value: `₡${data.rev1}`, align: 'right' },
          { key: 'rev2', label: 'Rev2', value: data.rev2 > 0 ? `₡${data.rev2}` : '\u00A0', align: 'right' },
        ];
      case 'MONAZOS':
        return [
          { key: 'nums', label: 'Números', value: `${data.d1}${data.d2}${data.d3}`, align: 'center' },
          { key: 'tipo', label: 'Tipo', value: data.tipo, align: 'center' },
          { key: 'monto', label: 'Monto', value: `₡${data.monto}`, align: 'right' },
        ];
      case 'FECHAS':
        return [
          { key: 'fecha', label: 'Fecha', value: `${data.dia}-${data.mes.slice(0, 3)}`, align: 'center' },
          { key: 'monto', label: 'Monto', value: `₡${data.monto}`, align: 'right' },
        ];
      case 'PARLEY':
        return [
          { key: 'n1', label: 'Num1', value: `#${data.num1}`, align: 'center' },
          { key: 'n2', label: 'Num2', value: `#${data.num2}`, align: 'center' },
          { key: 'monto', label: 'Monto', value: `₡${data.monto}`, align: 'right' },
        ];
    }
  };

  const headerCols = (() => {
    switch (modalidad) {
      case 'NORMAL':
        return ['Número', 'Monto'];
      case 'REVENTADO':
        return ['Número', 'Rev1', 'Rev2'];
      case 'MEGA_REVENTADO':
        return ['Número', 'Rev1', 'Rev2'];
      case 'MONAZOS':
        return ['Números', 'Tipo', 'Monto'];
      case 'FECHAS':
        return ['Fecha', 'Monto'];
      case 'PARLEY':
        return ['Num1', 'Num2', 'Monto'];
      default:
        return ['Dato', 'Monto'];
    }
  })();

  const gridTemplate = (() => {
    switch (modalidad) {
      case 'NORMAL':
        return 'minmax(0, 1fr) minmax(0, 1fr)';
      case 'REVENTADO':
        return 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';
      case 'MEGA_REVENTADO':
        return 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)';
      case 'MONAZOS':
        return 'minmax(0, 1fr) minmax(0, 0.8fr) minmax(0, 0.9fr)';
      case 'PARLEY':
        return 'minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1fr)';
      case 'FECHAS':
        return 'minmax(0, 1fr) minmax(0, 1fr)';
      default:
        return `repeat(${headerCols.length}, minmax(0, 1fr))`;
    }
  })();

  return (
    <div className="flex flex-col h-full bg-gray-900 rounded-lg border border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <span className={`${sorteoConfig.color} text-white text-xs font-bold px-2 py-0.5 rounded`}>
            {sorteoConfig.label}
          </span>
          <span className="text-gray-400 text-sm">
            {items.length} jugada{items.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {showViewListButton && (
            <button
              type="button"
              onClick={onViewList}
              className="text-xs font-bold text-gray-100 hover:text-white px-2.5 py-1.5 rounded border border-gray-700 bg-gray-800/60 hover:bg-gray-800/90"
            >
              Lista previa
            </button>
          )}
          {items.length > 0 && (
            <button
              onClick={onClear}
              className="text-xs text-red-400 hover:text-red-300"
            >
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* Items */}
      <div ref={listRef} className="flex-1 overflow-y-auto min-h-0">
        {items.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-600 text-sm">
            Sin jugadas
          </div>
        ) : (
          <div className="divide-y divide-gray-800">
            {/* Column header */}
            <div className="px-3 py-2 bg-gray-800/40 text-[10px] text-gray-400 uppercase tracking-wide">
              <div className="flex items-center gap-2">
                <div className="grid flex-1 gap-2" style={{ gridTemplateColumns: gridTemplate }}>
                  {headerCols.map(h => {
                    const isMoneyCol = h === 'Monto' || h === 'Rev1' || h === 'Rev2' || h === 'M';
                    return (
                      <span key={h} className={isMoneyCol ? 'text-right' : 'text-center'}>
                        {h}
                      </span>
                    );
                  })}
                </div>
                <div className="w-14 text-center">{modalidad === 'MONAZOS' ? 'X' : ''}</div>
              </div>
            </div>
            {displayItems.map(item => {
              const cols = getColumns(item.data);
              return (
                <div key={item.id} className="flex items-center px-3 py-2 hover:bg-gray-800/50 text-sm">
                  <div className="grid flex-1 gap-2" style={{ gridTemplateColumns: gridTemplate }}>
                    {cols.map(c => (
                      <span
                        key={c.key}
                        className={`font-mono ${c.key === 'monto' || c.key.startsWith('rev') ? 'text-green-400' : 'text-white'} ${
                          c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'
                        }`}
                      >
                        {c.value}
                      </span>
                    ))}
                  </div>
                  <div className="w-14 flex items-center justify-end gap-1">
                    <button
                      onClick={() => onEdit(item)}
                      className="p-1 text-gray-500 hover:text-yellow-400"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => onRemove(item.id)}
                      className="p-1 text-gray-500 hover:text-red-400"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Total */}
      <div className="border-t border-gray-700 px-3 py-3 bg-gray-800">
        <div className="flex items-center justify-between">
          <span className="text-gray-400 text-sm">TOTAL</span>
          <span className="text-2xl font-bold text-green-400 font-mono">
            ₡{total.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================
// PAYMENT SECTION
// ============================================
interface PaymentSectionProps {
  method: PaymentMethod;
  onMethodChange: (m: PaymentMethod) => void;
  total: number;
  onConfirm: () => void;
  disabled: boolean;
  showCredit: boolean;
  cliente: Cliente | null;
  onClienteChange: (c: Cliente | null) => void;
  referencia: string;
  onReferenciaChange: (r: string) => void;
}

const PaymentSection: React.FC<PaymentSectionProps> = ({
  method,
  onMethodChange,
  total,
  onConfirm,
  disabled,
  showCredit,
  cliente,
  onClienteChange,
  referencia,
  onReferenciaChange,
}) => {
  const [showClienteModal, setShowClienteModal] = useState(false);
  const [clientesList, setClientesList] = useState<Cliente[]>([]);

  useEffect(() => {
    creditoService.listarClientes().then(data => {
      setClientesList(data.filter(c => c.activo !== false && !c.bloqueado_credito));
    }).catch(() => setClientesList([]));
  }, []);

  const methods: { id: PaymentMethod; label: string }[] = [
    { id: 'EFECTIVO', label: 'Efectivo' },
    { id: 'SINPE', label: 'SINPE' },
    { id: 'TRANSFERENCIA', label: 'Transfer' },
    ...(showCredit ? [{ id: 'CREDITO' as PaymentMethod, label: 'Crédito' }] : []),
  ];

  const isCredito = method === 'CREDITO';
  const creditoValid = !isCredito || (cliente && referencia.trim());

  return (
    <div className="bg-gray-800 p-3 rounded-lg space-y-3">
      {/* Payment methods */}
      <div className="flex gap-1">
        {methods.map(m => (
          <button
            key={m.id}
            onClick={() => onMethodChange(m.id)}
            className={`
              flex-1 py-2 text-xs font-bold rounded transition-all
              ${method === m.id
                ? 'bg-yellow-500 text-black'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }
            `}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Credit options */}
      {isCredito && (
        <div className="space-y-2 p-2 bg-purple-900/30 rounded border border-purple-700">
          <button
            type="button"
            onClick={() => setShowClienteModal(true)}
            className="w-full py-2 text-sm bg-gray-700 text-white rounded hover:bg-gray-600 flex items-center justify-center gap-2"
          >
            <User size={16} />
            {cliente ? cliente.nombre : 'Seleccionar cliente'}
          </button>
          <input
            type="text"
            value={referencia}
            onChange={e => onReferenciaChange(e.target.value)}
            placeholder="Referencia (obligatorio)"
            className="w-full px-3 py-2 text-sm bg-gray-900 border border-gray-600 rounded text-white"
          />
          {(!cliente || !referencia.trim()) && (
            <p className="text-xs text-amber-400">⚠️ Cliente y referencia requeridos</p>
          )}
        </div>
      )}

      {/* Confirm button */}
      <button
        onClick={onConfirm}
        disabled={disabled || !creditoValid}
        className={`
          w-full py-4 text-lg font-bold rounded transition-all
          ${disabled || !creditoValid
            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
            : 'bg-green-600 text-white hover:bg-green-500'
          }
        `}
      >
        Confirmar ₡{total.toLocaleString()}
      </button>

      {/* Cliente Modal */}
      <Modal
        isOpen={showClienteModal}
        onClose={() => setShowClienteModal(false)}
        title="Seleccionar Cliente"
      >
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {clientesList.length === 0 ? (
            <p className="text-center text-gray-500 py-4 text-sm">No hay clientes disponibles</p>
          ) : clientesList.map(c => (
            <button
              key={c.id}
              onClick={() => {
                onClienteChange(c);
                setShowClienteModal(false);
              }}
              className={`
                w-full p-3 text-left rounded border transition-all
                ${cliente?.id === c.id
                  ? 'bg-purple-100 border-purple-500'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }
              `}
            >
              <p className="font-medium">{c.nombre}</p>
              <p className="text-xs text-gray-500">{c.telefono}</p>
            </button>
          ))}
        </div>
      </Modal>
    </div>
  );
};

// ============================================
// MAIN POS TERMINAL PAGE
// ============================================
export const POSTerminal: React.FC = () => {
  const { user } = useAuth();
  const [limiteApuestaMaxima, setLimiteApuestaMaxima] = useState<number | null>(
    user?.limiteApuestaMaxima ?? null
  );
  const limiteApuestaLabel = limiteApuestaMaxima == null
    ? 'Sin límite'
    : `₡${limiteApuestaMaxima.toLocaleString()}`;

  // State
  const [activeSorteo, setActiveSorteo] = useState<SorteoType>('TICA');
  const [ticketsBySorteo, setTicketsBySorteo] = useState<TicketsBySorteo>(createEmptyTicketsBySorteo);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('EFECTIVO');
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [referencia, setReferencia] = useState('');
  const [editingItem, setEditingItem] = useState<TicketItem | null>(null);
  const [successModal, setSuccessModal] = useState(false);
  const [lastTicketCode, setLastTicketCode] = useState('');
  const [amountResetKey, setAmountResetKey] = useState(0);
  const [paymentModal, setPaymentModal] = useState(false);
  // Cliente y Referencia: uncontrolled inputs (sin re-render al escribir)
  const modalClienteRef = useRef<Cliente | null>(null);
  const modalClienteNombreInputRef = useRef<HTMLInputElement>(null);
  const modalReferenciaInputRef = useRef<HTMLInputElement>(null);
  // Refs temporales para almacenar valores capturados
  const pendingClienteNombreRef = useRef<string>('');
  const pendingReferenciaRef = useRef<string>('');
  const [showClienteSelector, setShowClienteSelector] = useState(false);
  const [clienteSearch, setClienteSearch] = useState('');
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentNotice, setPaymentNotice] = useState<string | null>(null);
  const [riesgoAdvertencia, setRiesgoAdvertencia] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [apiClientes, setApiClientes] = useState<Cliente[]>([]);

  // Backend sorteos state
  const [backendSorteos, setBackendSorteos] = useState<BackendSorteo[]>([]);
  const [selectedSorteoId, setSelectedSorteoId] = useState<Record<SorteoType, string>>({
    TICA: '', REVENTADO: '', MEGA: '', MONAZOS: '', FECHAS: '', PARLEY: '',
  });

  const [ticketListModal, setTicketListModal] = useState(false);

  // Sidebar live data
  const [sidebarTop, setSidebarTop] = useState<Array<{ numero: string; total: number }>>([]);
  const [sidebarRestringidos, setSidebarRestringidos] = useState<string[]>([]);
  const [sidebarLimites, setSidebarLimites] = useState<{
    monto_minimo: number | null;
    monto_maximo: number | null;
    limite_por_numero: number | null;
    limite_total: number | null;
  } | null>(null);

  const [anularTicketModal, setAnularTicketModal] = useState(false);
  const [reimpresionModal, setReimpresionModal] = useState(false);

  useEffect(() => {
    fetchAPI<{ monto: number | null }>('/configuracion/maximo-apuesta')
      .then((data) => setLimiteApuestaMaxima(data.monto ?? null))
      .catch(() => {
        setLimiteApuestaMaxima(user?.limiteApuestaMaxima ?? null);
      });
  }, [user?.limiteApuestaMaxima]);

  // Ticket preview/print state
  const [ticketPreviewPayload, setTicketPreviewPayload] = useState<TicketPayload | null>(null);

  const [pullTicketModal, setPullTicketModal] = useState(false);
  const [pullTicketCode, setPullTicketCode] = useState('');
  const [pullTicketError, setPullTicketError] = useState<string | null>(null);
  const pullTicketInputRef = useRef<HTMLInputElement>(null);

  const ticketHistoryRef = useRef<
    Map<string, { sorteo: SorteoType; createdAt: number; items: TicketItemData[] }>
  >(new Map());

  // Ref para handleConfirm (para usar en keyboard shortcuts)
  const handleConfirmRef = useRef<() => Promise<string | null>>(null);

  // ── Caja gate (VENDEDOR_CON_CAJA) ──────────────
  const needsCaja = user?.role === 'VENDEDOR_CON_CAJA';
  const [cajaEstado, setCajaEstado] = useState<'LOADING' | 'ABIERTA' | 'CERRADA' | 'NO_INICIADA'>(needsCaja ? 'LOADING' : 'ABIERTA');
  const [cajaLoading, setCajaLoading] = useState(false);
  const [cajaError, setCajaError] = useState<string | null>(null);
  const [saldoInicialInput, setSaldoInicialInput] = useState('');

  useEffect(() => {
    if (!needsCaja) return;
    const hoy = fechaLocalYMD();
    getEstadoCierre(hoy)
      .then((res) => setCajaEstado(res.caja_estado))
      .catch(() => setCajaEstado('NO_INICIADA'));
  }, [needsCaja]);

  // Auto-focus al input correcto cuando se abre el modal de pago
  useEffect(() => {
    if (!paymentModal) return;

    // Pequeño delay para que el modal se renderice completamente
    const timer = setTimeout(() => {
      if (paymentMethod === 'CREDITO') {
        // CREDITO: no hay input de texto, foco no aplica (se selecciona cliente con botón)
        return;
      }
      // EFECTIVO, SINPE, TRANSFERENCIA: foco en el input de cliente
      modalClienteNombreInputRef.current?.focus();
    }, 80);

    return () => clearTimeout(timer);
  }, [paymentModal, paymentMethod]);

  const handleAbrirCaja = async () => {
    setCajaLoading(true);
    setCajaError(null);
    try {
      const saldo = parseFloat(saldoInicialInput.replace(/,/g, '')) || 0;
      await abrirCaja(saldo);
      setCajaEstado('ABIERTA');
    } catch (err: any) {
      setCajaError(err.message || 'Error al abrir caja');
    } finally {
      setCajaLoading(false);
    }
  };

  const canUseCredit = user?.role === 'VENDEDOR_CON_CAJA' || user?.role === 'LOCAL';
  const currentItems = ticketsBySorteo[activeSorteo];
  const total = currentItems.reduce((sum, item) => sum + calcularTotalItem(item.data), 0);

  // Sorteos agrupados por tipo para el tab activo
  const sorteosByTipo = useCallback((tipo: SorteoType): BackendSorteo[] => {
    const tipoId = tipoIdFromSorteoType(tipo);
    // Mostrar SOLO sorteos ABIERTOS - sorteos cerrados no aparecen en la lista
    return backendSorteos.filter(s => s.tipo_id === tipoId && s.estado === 'ABIERTO');
  }, [backendSorteos]);

  const currentSorteoId = selectedSorteoId[activeSorteo];

  // ── Sorteo cerrado (local countdown expiró) ──
  const [sorteoCerradoLocal, setSorteoCerradoLocal] = useState(false);

  useEffect(() => {
    setSorteoCerradoLocal(false);
  }, [activeSorteo, currentSorteoId]);

  const isSorteoClosed = (() => {
    const currentBackend = backendSorteos.find(s => s.id === currentSorteoId);
    return currentBackend?.estado !== 'ABIERTO' || sorteoCerradoLocal;
  })();

  const isSorteoClosedRef = useRef(false);
  isSorteoClosedRef.current = isSorteoClosed;

  const paymentMethodRef = useRef(paymentMethod);
  paymentMethodRef.current = paymentMethod;

  const paymentModalRef = useRef(paymentModal);
  paymentModalRef.current = paymentModal;

  const currentItemsRef = useRef(currentItems);
  currentItemsRef.current = currentItems;

  const cargarSorteosBackend = useCallback(() => {
    fetchAPI<BackendSorteo[]>('/sorteos', { timeoutMs: 20000 })
      .then((sorteos) => {
        setBackendSorteos(sorteos);
        setSelectedSorteoId((prev) => {
          const next = { ...prev };
          for (const tipo of SORTEO_CONFIG) {
            const tipoId = tipoIdFromSorteoType(tipo.id);
            const matching = sorteos.filter(s => s.tipo_id === tipoId && s.estado === 'ABIERTO');
            const current = next[tipo.id];
            const sigueVigente = current && sorteos.some(s => s.id === current && s.estado === 'ABIERTO');
            if (!sigueVigente) {
              next[tipo.id] = matching.length > 0 ? matching[0].id : '';
            }
          }
          return next;
        });
      })
      .catch((err) => {
        if (err?.message !== 'Conexion lenta. Intente nuevamente.') {
          console.error('Error cargando sorteos:', err);
        }
      });
  }, []);

  // Fetch sorteos from backend on mount
  useEffect(() => {
    cargarSorteosBackend();
    const interval = window.setInterval(cargarSorteosBackend, 15000);
    const onFocus = () => cargarSorteosBackend();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [cargarSorteosBackend]);

  // Sidebar: fetch top-vendidos + restringidos when sorteo changes
  useEffect(() => {
    if (!currentSorteoId) {
      setSidebarTop([]);
      setSidebarRestringidos([]);
      setSidebarLimites(null);
      return;
    }
    const load = () => {
      fetchAPI<{ top: Array<{ numero: string; total: number }>; restringidos: string[]; limites: { monto_minimo: number | null; monto_maximo: number | null; limite_por_numero: number | null; limite_total: number | null } | null }>(
        `/listas/top-vendidos/${currentSorteoId}`
      )
        .then((data) => {
          setSidebarTop(data.top || []);
          setSidebarRestringidos(data.restringidos || []);
          setSidebarLimites(data.limites || null);
        })
        .catch(() => {
          setSidebarTop([]);
          setSidebarRestringidos([]);
          setSidebarLimites(null);
        });
    };
    load();
    const iv = window.setInterval(load, 30000);
    return () => window.clearInterval(iv);
  }, [currentSorteoId]);

  // Cargar clientes desde API (para selector de crédito)
  useEffect(() => {
    creditoService.listarClientes().then(data => {
      setApiClientes(data.filter(c => c.activo !== false && !c.bloqueado_credito));
    }).catch(() => setApiClientes([]));
  }, []);

  // Cargar historial desde localStorage (si existe)
  useEffect(() => {
    try {
      const raw = localStorage.getItem('pos.ticketHistory.v1');
      if (!raw) return;
      const arr = JSON.parse(raw) as Array<{ code: string; sorteo: SorteoType; createdAt: number; items: TicketItemData[] }>;
      if (!Array.isArray(arr)) return;
      const map = new Map<string, { sorteo: SorteoType; createdAt: number; items: TicketItemData[] }>();
      for (const it of arr) {
        if (!it?.code || !it?.sorteo || !Array.isArray(it.items)) continue;
        map.set(String(it.code).toUpperCase(), { sorteo: it.sorteo, createdAt: it.createdAt || Date.now(), items: it.items });
      }
      ticketHistoryRef.current = map;
    } catch {
      // ignore
    }
  }, []);

  const persistTicketHistory = useCallback(() => {
    try {
      const entries: Array<{ code: string; sorteo: SorteoType; createdAt: number; items: TicketItemData[] }> = [];
      for (const [code, v] of ticketHistoryRef.current.entries()) {
        entries.push({ code, sorteo: v.sorteo, createdAt: v.createdAt, items: v.items });
      }
      entries.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      localStorage.setItem('pos.ticketHistory.v1', JSON.stringify(entries.slice(0, 50)));
    } catch {
      // ignore
    }
  }, []);

  // Get ticket counts
  const ticketCounts = Object.fromEntries(
    SORTEO_CONFIG.map(s => [s.id, ticketsBySorteo[s.id].length])
  ) as Record<SorteoType, number>;

  // Handlers
  const handleAddItem = useCallback((data: TicketItemData) => {
    const newItem: TicketItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      data,
      timestamp: Date.now(),
    };
    setTicketsBySorteo(prev => ({
      ...prev,
      [activeSorteo]: [...prev[activeSorteo], newItem],
    }));
  }, [activeSorteo]);

  const handleEditItem = useCallback((item: TicketItem) => {
    setEditingItem(item);
  }, []);

  const handleSaveEdit = useCallback((data: TicketItemData) => {
    if (!editingItem) return;
    setTicketsBySorteo(prev => ({
      ...prev,
      [activeSorteo]: prev[activeSorteo].map(item =>
        item.id === editingItem.id ? { ...item, data } : item
      ),
    }));
    setEditingItem(null);
  }, [activeSorteo, editingItem]);

  const handleRemoveItem = useCallback((id: string) => {
    setTicketsBySorteo(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated) as SorteoType[]) {
        const before = updated[key].length;
        updated[key] = updated[key].filter(item => item.id !== id);
        if (updated[key].length < before) break; // found and removed
      }
      return updated;
    });
  }, []);

  const handleClearTicket = useCallback(() => {
    setTicketsBySorteo(prev => ({
      ...prev,
      [activeSorteo]: [],
    }));

    setEditingItem(null);
    setAmountResetKey(prev => prev + 1);
  }, [activeSorteo, setEditingItem, setAmountResetKey]);

  // Extraer solo números de un TicketItemData (sin sorteo, sin monto, sin modalidad)
  const extractNumerosFromItem = useCallback((data: TicketItemData): string[] => {
    switch (data.modalidad) {
      case 'NORMAL':       return [data.numero];
      case 'REVENTADO':    return [data.numero];
      case 'MEGA_REVENTADO': return [data.numero];
      case 'MONAZOS':      return [`${data.d1}${data.d2}${data.d3}`];
      case 'FECHAS':       return [`${String(data.dia).padStart(2, '0')}`];
      case 'PARLEY':       return [data.num1, data.num2];
      default:             return [];
    }
  }, []);

  const pullTicketByCode = useCallback((rawCode: string) => {
    const code = rawCode.replace(/\D/g, '').trim();
    if (!code) {
      setPullTicketError('Ingresa el número de ticket');
      return;
    }

    let found = ticketHistoryRef.current.get(code);
    if (!found) {
      // fallback a localStorage por si el map no está actualizado
      try {
        const raw = localStorage.getItem('pos.ticketHistory.v1');
        if (raw) {
          const arr = JSON.parse(raw) as Array<{ code: string; sorteo: SorteoType; createdAt: number; items: TicketItemData[] }>;
          const hit = Array.isArray(arr)
            ? arr.find(x => String(x.code || '').replace(/\D/g, '') === code)
            : undefined;
          if (hit && Array.isArray(hit.items)) {
            found = { sorteo: hit.sorteo, createdAt: hit.createdAt || Date.now(), items: hit.items };
            ticketHistoryRef.current.set(code, found);
          }
        }
      } catch {
        // ignore
      }
    }

    if (!found) {
      setPullTicketError('Ticket no encontrado');
      return;
    }

    // Jalar números Y montos del ticket original, adaptando al sorteo destino si es necesario
    const sourceSorteo = found.sorteo;
    const rebuiltItems: TicketItem[] = found.items.map(data => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      data: sourceSorteo !== activeSorteo ? adaptarItemParaSorteo(data, activeSorteo) : data,
      timestamp: Date.now(),
    }));

    // NO cambiar sorteo activo — el vendedor decide dónde asignar los números
    setTicketsBySorteo(prev => ({
      ...prev,
      [activeSorteo]: [...prev[activeSorteo], ...rebuiltItems],
    }));
    setEditingItem(null);
    setPullTicketModal(false);
    setPullTicketCode('');
    setPullTicketError(null);
    // Importante: solo cargamos los números para editar; NO confirmamos venta.
    setPaymentModal(false);
    setPaymentError(null);
    setPaymentNotice(null);
  }, [activeSorteo, extractNumerosFromItem]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // REGLA #1: Detectar si estamos escribiendo en un input/textarea/select
      const target = e.target as HTMLElement;
      const isTypingInInput = 
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable ||
        false;

      // Detectar tecla de impresión (Shift+8 o Numpad *)
      const isPrintKey = e.key === '*' || e.code === 'NumpadMultiply';

      // Si estamos escribiendo, permitir Escape, * (para imprimir), y Enter en modal de pago
      // Todo lo demás lo maneja el input normalmente
      if (isTypingInInput && !isPrintKey && e.key !== 'Escape') {
        if (!(e.key === 'Enter' && paymentModalRef.current)) {
          return; // Dejar que el input procese el evento
        }
      }

      // ESC - cerrar modales
      if (e.key === 'Escape') {
        if (ticketPreviewPayload) { setTicketPreviewPayload(null); return; }
        if (successModal) setSuccessModal(false);
        if (paymentModalRef.current) setPaymentModal(false);
        if (pullTicketModal) setPullTicketModal(false);
        if (showClienteSelector) setShowClienteSelector(false);
        if (anularTicketModal) setAnularTicketModal(false);
        if (reimpresionModal) setReimpresionModal(false);
        return;
      }

      // No procesar shortcuts si estamos en otros modales (excepto paymentModal)
      if (ticketPreviewPayload || successModal || showClienteSelector || pullTicketModal || anularTicketModal || reimpresionModal) return;

      // * (asterisco) o Enter en modal - confirmar venta o navegar campos
      if ((isPrintKey || e.key === 'Enter') && paymentModalRef.current) {
        e.preventDefault();
        if (isSorteoClosedRef.current) return;

        const metodo = paymentMethodRef.current;

        // Enter navega entre campos según método de pago
        if (e.key === 'Enter') {
          const activeEl = document.activeElement;
          const isInCliente = activeEl === modalClienteNombreInputRef.current;

          // SINPE / TRANSFERENCIA: cliente → referencia → confirmar
          if (metodo === 'SINPE' || metodo === 'TRANSFERENCIA') {
            if (isInCliente) {
              modalReferenciaInputRef.current?.focus();
              return;
            }
            // Si está en referencia o en otro lugar → confirmar (cae al bloque de abajo)
          }

          // EFECTIVO: Enter → confirmar directamente
          // CREDITO: Enter → confirmar directamente
        }

        if (currentItemsRef.current.length === 0) {
          setPaymentError('No hay jugadas. Usa “Jalar ticket” o captura una jugada.');
          return;
        }
        // Validar según método de pago - leer de input refs
        const referenciaValue = modalReferenciaInputRef.current?.value?.trim() || '';
        const canConfirm = metodo === 'EFECTIVO' ||
          ((metodo === 'SINPE' || metodo === 'TRANSFERENCIA') && referenciaValue) ||
          (metodo === 'CREDITO' && modalClienteRef.current);

        if (!canConfirm) {
          setPaymentError(metodo === 'CREDITO' ? 'Selecciona un cliente' : 'Ingresa la referencia');
          return;
        }

        // ⚠️ CAPTURAR VALORES ANTES DE CUALQUIER SETSTATE QUE CIERRE EL MODAL
        const clienteNombreACapturar = modalClienteNombreInputRef.current?.value?.trim() || '';
        const referenciaACapturar = referenciaValue;
        
        pendingClienteNombreRef.current = clienteNombreACapturar;
        pendingReferenciaRef.current = referenciaACapturar;

        setPaymentError(null);
        setPaymentNotice(null);
        setRiesgoAdvertencia(null);
        setPaymentModal(false);
        setCliente(modalClienteRef.current);
        setReferencia(referenciaValue);
        handleConfirmRef.current?.();
        return;
      }

      // * (asterisco) - abrir modal de pago
      if (isPrintKey && !paymentModalRef.current) {
        e.preventDefault();
        if (isSorteoClosedRef.current) return;
        // Limpiar inputs (uncontrolled)
        if (modalClienteNombreInputRef.current) {
          modalClienteNombreInputRef.current.value = '';
        }
        if (modalReferenciaInputRef.current) {
          modalReferenciaInputRef.current.value = '';
        }
        modalClienteRef.current = null;
        setPaymentError(null);
        setPaymentNotice(null);
        setRiesgoAdvertencia(null);
        setPaymentModal(true);
        return;
      }

      // - (guión) - limpiar ticket
      if (e.key === '-' && !paymentModalRef.current) {
        e.preventDefault();
        handleClearTicket();
        return;
      }

      // F2 - cambiar método de pago
      if (e.key === 'F2') {
        e.preventDefault();
        const methods: PaymentMethod[] = canUseCredit 
          ? ['EFECTIVO', 'SINPE', 'TRANSFERENCIA', 'CREDITO']
          : ['EFECTIVO', 'SINPE', 'TRANSFERENCIA'];
        const currentIdx = methods.indexOf(paymentMethod);
        const nextIdx = (currentIdx + 1) % methods.length;
        setPaymentMethod(methods[nextIdx]);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleConfirm = useCallback(async (): Promise<string | null> => {
    if (currentItems.length === 0) return null;

    const sorteoId = selectedSorteoId[activeSorteo];
    if (!sorteoId) {
      setPaymentError('No hay sorteo seleccionado para este tipo');
      setPaymentModal(true);
      return null;
    }

    const jugadas = itemsToJugadas(currentItems.map(i => i.data));
    if (jugadas.length === 0) return null;

    if (limiteApuestaMaxima != null) {
      const jugadaExcedida = jugadas.find((jugada) => Number(jugada.monto) > limiteApuestaMaxima);
      if (jugadaExcedida) {
        const mensaje = `Excede el máximo permitido. Máximo por apuesta: ₡${limiteApuestaMaxima.toLocaleString()}`;
        setPaymentError(mensaje);
        setPaymentModal(true);
        return null;
      }
    }

    try {
      const montoPorNumero = new Map<string, number>();
      for (const jugada of jugadas) {
        montoPorNumero.set(
          jugada.num1,
          (montoPorNumero.get(jugada.num1) || 0) + Number(jugada.monto || 0),
        );
      }

      const revisiones = await Promise.all(
        Array.from(montoPorNumero.entries()).map(([numero, monto]) =>
          fetchRiesgoNumero(numero, monto, sorteoId)
            .then(resultado => ({ numero, resultado }))
            .catch(() => null)
        )
      );

      const evaluaciones = revisiones.filter(
        (item): item is { numero: string; resultado: Awaited<ReturnType<typeof fetchRiesgoNumero>> } => item !== null
      );

      const riesgoAlto = evaluaciones.find(item => item.resultado.nivel === 'ALTO');
      const riesgoMedio = evaluaciones.find(item => item.resultado.nivel === 'MEDIO');

      if (riesgoAlto) {
        setRiesgoAdvertencia(
          riesgoAlto.resultado.mensaje ||
          `Advertencia: el número ${riesgoAlto.numero} queda con riesgo ALTO.`
        );
      } else if (riesgoMedio) {
        setRiesgoAdvertencia(
          riesgoMedio.resultado.mensaje ||
          `Advertencia: el número ${riesgoMedio.numero} queda con riesgo MEDIO.`
        );
      } else {
        setRiesgoAdvertencia(null);
      }
    } catch {
      setRiesgoAdvertencia(null);
    }

    setSubmitting(true);
    try {
      const clienteNombreCapturado = paymentMethod === 'CREDITO'
        ? (modalClienteRef.current?.nombre || 'Cliente')
        : (pendingClienteNombreRef.current || modalClienteNombreInputRef.current?.value?.trim() || 'Cliente');

      const ticket = await fetchAPI<any>('/tickets', {
        method: 'POST',
        headers: {
          'X-POS-Client': 'tiempos-pos-v2'
        },
        body: JSON.stringify({
          sorteo_id: sorteoId,
          vendedor_id: user?.id || 'VENDEDOR-1',
          limiteApuestaMaxima,
          local_id: user?.localId || 'LOCAL-1',
          metodo_pago: paymentMethod,
          cliente_id: paymentMethod === 'CREDITO' ? modalClienteRef.current?.id : null,
          cliente_nombre: clienteNombreCapturado,
          referencia: (paymentMethod === 'SINPE' || paymentMethod === 'TRANSFERENCIA') ? (pendingReferenciaRef.current || modalReferenciaInputRef.current?.value?.trim()) : null,
          jugadas,
        }),
      });

      const code = ticket.ticket_number || ticket.id;

      // Notificar a listas para refresh optimista inmediato (sin esperar polling)
      emitirVenta({
        sorteo_id: sorteoId,
        tipo_id: activeSorteo,
        jugadas: jugadas.map((j: any) => ({ num1: j.num1, num2: j.num2 ?? null, monto: j.monto })),
      });

      // Guardar historial local para poder "jalar" el ticket luego
      ticketHistoryRef.current.set(code, {
        sorteo: activeSorteo,
        createdAt: Date.now(),
        items: currentItems.map(i => i.data),
      });
      persistTicketHistory();

      setLastTicketCode(code);

      // ── POST-VENTA: generar preview del ticket ──
      const sorteoInfo = backendSorteos.find(s => s.id === sorteoId);
      const ticketJugadas: TicketJugada[] = jugadas.map(j => ({
        num1: j.num1,
        num2: j.num2 || null,
        monto: j.monto,
      }));

      // Fecha del sorteo = fecha de venta (hoy) en DD/MM/YYYY
      const hoy = new Date();
      const sorteoFechaStr = `${String(hoy.getDate()).padStart(2, '0')}/${String(hoy.getMonth() + 1).padStart(2, '0')}/${hoy.getFullYear()}`;

      // Obtener pagaVeces de TODOS los conceptos disponibles
      let pagaVeces: string | null = null;
      try {
        const sorteoNombreParam = encodeURIComponent(sorteoInfo?.nombre || activeSorteo);
        const conceptos = ['DIRECTO', 'REVENTADO', 'MEGA_REVENTADO'];
        const labelMap: Record<string, string> = { DIRECTO: 'Normal', REVENTADO: 'Reventado', MEGA_REVENTADO: 'Mega' };
        const promises = conceptos.map(c =>
          fetchAPI<{ paga_local: number | null; paga_banca: number | null }>(
            `/pago-vendedor/resolver?sorteo_nombre=${sorteoNombreParam}&concepto=${c}`
          ).catch(() => null)
        );
        const responses = await Promise.all(promises);
        const parts: string[] = [];
        responses.forEach((res, i) => {
          if (!res) return;
          const paga = res.paga_local ?? res.paga_banca;
          if (paga != null && Number(paga) > 0) {
            parts.push(`${labelMap[conceptos[i]] || conceptos[i]} ${paga}`);
          }
        });
        if (parts.length > 0) pagaVeces = parts.join(' | ');
      } catch { /* no disponible, omitir */ }

      try {
        const payload = await procesarPostVenta({
          ticketCode: code,
          ticketId: ticket.id || code,
          sorteoNombre: sorteoInfo?.nombre || activeSorteo,
          sorteoCierre: sorteoInfo?.hora_cierre || '',
          sorteoFecha: sorteoFechaStr,
          metodoPago: paymentMethod,
          referencia: (paymentMethod === 'SINPE' || paymentMethod === 'TRANSFERENCIA') ? pendingReferenciaRef.current : null,
          total: currentItems.reduce((sum, item) => sum + calcularTotalItem(item.data), 0),
          jugadas: ticketJugadas,
          vendedorNombre: user?.nombre || '',
          localNombre: '',
          pagaVecesAplicado: pagaVeces,
          clienteNombre: paymentMethod === 'CREDITO'
            ? (modalClienteRef.current?.nombre || 'Cliente')
            : (pendingClienteNombreRef.current || 'Cliente'),
        });
        setTicketPreviewPayload(payload);
      } catch (previewErr) {
        console.error('[POSTerminal] Error generando preview:', previewErr);
        // Fallback: mostrar success modal simple
        setSuccessModal(true);
      }

      // Clear current sorteo ticket
      setTicketsBySorteo(prev => ({
        ...prev,
        [activeSorteo]: [],
      }));
      setCliente(null);
      setReferencia('');
      // Limpiar inputs uncontrolled del modal
      if (modalClienteNombreInputRef.current) {
        modalClienteNombreInputRef.current.value = '';
      }
      if (modalReferenciaInputRef.current) {
        modalReferenciaInputRef.current.value = '';
      }
      modalClienteRef.current = null;
      setPaymentMethod('EFECTIVO');
      setAmountResetKey(prev => prev + 1);

      return code;
    } catch (error: any) {
      setPaymentError(error.message || 'Error al crear ticket');
      setPaymentModal(true);
      return null;
    } finally {
      setSubmitting(false);
    }
  }, [currentItems, activeSorteo, selectedSorteoId, persistTicketHistory, paymentMethod, user, backendSorteos, limiteApuestaMaxima]);

  // Actualizar ref de handleConfirm (useLayoutEffect para evitar race condition con keydown)
  useLayoutEffect(() => {
    handleConfirmRef.current = handleConfirm;
  }, [handleConfirm]);

  // ── Gate de caja: pantalla bloqueante para VENDEDOR_CON_CAJA ──
  if (needsCaja && cajaEstado !== 'ABIERTA') {
    return (
      <div className="min-h-[calc(100vh-120px)] lg:h-[calc(100vh-80px)] flex items-center justify-center bg-gray-950 -m-4 lg:-m-6 p-4">
        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 max-w-md w-full text-center space-y-4">
          {cajaEstado === 'LOADING' ? (
            <>
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-gray-400">Verificando estado de caja...</p>
            </>
          ) : cajaEstado === 'NO_INICIADA' ? (
            <>
              <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <path d="M2 10h20" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white">Abrir Caja</h2>
              <p className="text-gray-400 text-sm">
                Debe abrir la caja del dia antes de realizar ventas.
              </p>
              <div className="text-left">
                <label className="block text-gray-400 text-xs mb-1">Dinero inicial (opcional)</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={saldoInicialInput}
                  onChange={e => setSaldoInicialInput(e.target.value)}
                  placeholder="₡0"
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-center text-lg font-mono focus:outline-none focus:border-blue-500"
                />
              </div>
              {cajaError && (
                <p className="text-red-400 text-sm bg-red-900/30 rounded-lg p-2">{cajaError}</p>
              )}
              <button
                onClick={handleAbrirCaja}
                disabled={cajaLoading}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 text-white font-bold rounded-xl transition-colors"
              >
                {cajaLoading ? 'Abriendo...' : 'Abrir Caja'}
              </button>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white">Caja Cerrada</h2>
              <p className="text-gray-400 text-sm">
                La caja del dia ya fue cerrada. Solicite reapertura a su supervisor (LOCAL).
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="lg:h-[calc(100vh-80px)] flex flex-col bg-gray-950 -m-4 lg:-m-6 p-2 lg:p-4">
      {/* Sorteo tabs */}
      <SorteoTabs
        active={activeSorteo}
        onChange={setActiveSorteo}
        ticketCounts={ticketCounts}
      />

      {/* Sorteo selector (horizontal buttons) + countdown timer */}
      {(() => {
        const available = sorteosByTipo(activeSorteo);
        if (available.length === 0) {
          return (
            <div className="px-3 py-1.5 bg-red-900/40 text-red-300 text-xs rounded-b-lg border border-t-0 border-gray-700">
              Sin sorteos abiertos para {SORTEO_CONFIG.find(s => s.id === activeSorteo)?.label}
            </div>
          );
        }
        const selectedSorteo = available.find(s => s.id === currentSorteoId) ?? available[0];
        return (
          <div className="px-3 py-1.5 bg-gray-800 text-xs rounded-b-lg border border-t-0 border-gray-700 flex items-center gap-2 flex-wrap">
            {available.map(s => (
              <button
                key={s.id}
                onClick={() => setSelectedSorteoId(prev => ({ ...prev, [activeSorteo]: s.id }))}
                className={`px-3 py-1.5 rounded font-semibold transition-all text-xs ${
                  s.id === (currentSorteoId || available[0].id)
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {s.nombre}
              </button>
            ))}
            <SorteoCountdown
              horaCierre={selectedSorteo.hora_cierre}
              fecha={selectedSorteo.fecha}
              estado={selectedSorteo.estado}
              onExpired={() => setSorteoCerradoLocal(true)}
            />
          </div>
        );
      })()}

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:flex-row gap-3 mt-3 lg:min-h-0">
        {/* Left - Capture form */}
        <div className="lg:w-80 lg:flex-shrink-0">
          <div className="bg-gray-900 rounded-lg p-4 border border-gray-700">
            <h3 className="text-gray-400 text-xs uppercase tracking-wide mb-3">
              Captura Rápida - {SORTEO_CONFIG.find(s => s.id === activeSorteo)?.label}
            </h3>
            <CaptureForm
              sorteo={activeSorteo}
              onAdd={handleAddItem}
              editingItem={editingItem}
              onCancelEdit={() => setEditingItem(null)}
              onSaveEdit={handleSaveEdit}
              resetAmountKey={amountResetKey}
              disabled={isSorteoClosed}
            />

            {/* Botón IMPRIMIR principal */}
            <button
              type="button"
              disabled={isSorteoClosed}
              onClick={() => {
                if (isSorteoClosed) return;
                // Limpiar inputs uncontrolled
                if (modalClienteNombreInputRef.current) {
                  modalClienteNombreInputRef.current.value = '';
                }
                if (modalReferenciaInputRef.current) {
                  modalReferenciaInputRef.current.value = '';
                }
                modalClienteRef.current = null;
                setPaymentError(null);
                setPaymentNotice(null);
                setRiesgoAdvertencia(null);
                setPaymentModal(true);
              }}
              className={`w-full mt-3 py-3.5 px-4 font-bold text-base rounded-lg transition-all border shadow-lg ${
                isSorteoClosed
                  ? 'bg-gray-700 text-gray-500 border-gray-600 cursor-not-allowed'
                  : 'bg-orange-600 hover:bg-orange-500 text-white border-orange-500'
              }`}
            >
              ★ IMPRIMIR (*)
            </button>
          </div>
        </div>

        {/* Center - Ticket (aligned left, leaves room for sidebar on large screens) */}
        <div className="flex-1 flex flex-col lg:min-h-0 min-w-0">
          <div className="min-h-[280px] lg:flex-1 lg:min-h-0">
            <TicketPanel
              sorteo={activeSorteo}
              items={currentItems}
              onEdit={handleEditItem}
              onRemove={handleRemoveItem}
              onClear={handleClearTicket}
              onViewList={() => setTicketListModal(true)}
            />
          </div>

          {/* Pago/Confirmación se hace por modal de impresión rápida (*) */}
        </div>

        {/* Right - Sidebar: Top 10 + Restringidos */}
        <aside className="hidden lg:block lg:w-56 xl:w-64 flex-shrink-0 min-h-0 overflow-y-auto">
          <div className="flex flex-col gap-2">
            {/* Top 10 */}
            <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
              <div className="px-2 py-1.5 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-xs font-bold text-white">Top 10</h3>
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 border border-gray-700">Hoy</span>
              </div>
              <div className="divide-y divide-gray-800">
                {sidebarTop.length === 0 && (
                  <p className="text-[10px] text-gray-500 px-2 py-2 text-center">Sin ventas</p>
                )}
                {sidebarTop.map((row, idx) => {
                  const isRestricted = sidebarRestringidos.includes(row.numero);
                  return (
                    <div
                      key={`${row.numero}-${idx}`}
                      className={`flex items-center justify-between px-2 py-1 ${isRestricted ? 'bg-red-950/40' : ''}`}
                    >
                      <span className={`font-mono text-xs font-bold ${isRestricted ? 'text-red-400' : 'text-white'}`}>
                        {row.numero}
                      </span>
                      <span className="text-[10px] font-mono text-green-400">
                        ₡{row.total.toLocaleString()}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Restringidos / Límites */}
            {activeSorteo === 'MONAZOS' || activeSorteo === 'FECHAS' || activeSorteo === 'PARLEY' ? (
              /* Límites de venta para tipos sin restringidos */
              <div className="bg-gray-900 rounded-lg border border-yellow-900/50 overflow-hidden">
                <div className="px-2 py-1.5 border-b border-gray-700">
                  <h3 className="text-xs font-bold text-yellow-400">Límites</h3>
                </div>
                <div className="px-2 py-1.5">
                  <div className="space-y-1">
                    <div className="flex justify-between text-[11px]">
                      <span className="text-gray-400">Máximo por apuesta</span>
                      <span className="font-mono text-yellow-300">{limiteApuestaLabel}</span>
                    </div>
                    {!sidebarLimites || !sidebarLimites.limite_por_numero ? (
                      <p className="text-[10px] text-gray-500 text-center">Sin límites adicionales configurados</p>
                    ) : (
                      <>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-gray-400">Límite por jugada</span>
                        <span className="font-mono text-yellow-300">₡{sidebarLimites.limite_por_numero.toLocaleString()}</span>
                      </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Restringidos para tipos con restricciones */
              <div className="bg-gray-900 rounded-lg border border-red-900/50 overflow-hidden">
                <div className="px-2 py-1.5 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-xs font-bold text-red-400">Restringidos</h3>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-900/30 text-red-300 border border-red-800">
                    {sidebarRestringidos.length}
                  </span>
                </div>
                <div className="px-2 py-1.5">
                  <div className="flex justify-between text-[11px] mb-2">
                    <span className="text-gray-400">Máximo por apuesta</span>
                    <span className="font-mono text-red-300">{limiteApuestaLabel}</span>
                  </div>
                  {sidebarRestringidos.length === 0 ? (
                    <p className="text-[10px] text-gray-500 text-center">Ninguno</p>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {sidebarRestringidos.map((n) => (
                        <span
                          key={n}
                          className="font-mono text-[11px] font-bold text-red-400 bg-red-950/50 border border-red-800/60 rounded px-1.5 py-0.5"
                        >
                          {n}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Pago/Confirmación se hace por modal de impresión rápida (*) */}

      {/* Lista previa modal (top vendidos + restringidos para móviles) */}
      <Modal
        isOpen={ticketListModal}
        onClose={() => setTicketListModal(false)}
        title="Lista previa"
        size="full"
      >
        <div className="h-[70vh] overflow-y-auto space-y-4 p-2">
          {/* Top vendidos */}
          <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-sm font-bold text-white">Más vendidos</h3>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 border border-gray-700">Hoy</span>
            </div>
            <div className="divide-y divide-gray-800">
              {sidebarTop.length === 0 ? (
                <p className="text-sm text-gray-500 px-3 py-4 text-center">No hay ventas registradas todavía</p>
              ) : (
                sidebarTop.map((row, idx) => {
                  const isRestricted = sidebarRestringidos.includes(row.numero);
                  return (
                    <div
                      key={`${row.numero}-${idx}`}
                      className={`flex items-center justify-between px-3 py-2 ${isRestricted ? 'bg-red-950/40' : ''}`}
                    >
                      <span className={`font-mono text-sm font-bold ${isRestricted ? 'text-red-400' : 'text-white'}`}>
                        {row.numero}
                      </span>
                      <span className="text-sm font-mono text-green-400">
                        ₡{row.total.toLocaleString()}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Restringidos / Límites */}
          {activeSorteo === 'MONAZOS' || activeSorteo === 'FECHAS' || activeSorteo === 'PARLEY' ? (
            <div className="bg-gray-900 rounded-lg border border-yellow-900/50 overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-700">
                <h3 className="text-sm font-bold text-yellow-400">Límites</h3>
              </div>
              <div className="px-3 py-2">
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Máximo por apuesta</span>
                    <span className="font-mono text-yellow-300">{limiteApuestaLabel}</span>
                  </div>
                  {!sidebarLimites || !sidebarLimites.limite_por_numero ? (
                    <p className="text-sm text-gray-500 text-center">Sin límites adicionales configurados</p>
                  ) : (
                    <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Límite por jugada</span>
                      <span className="font-mono text-yellow-300">₡{sidebarLimites.limite_por_numero.toLocaleString()}</span>
                    </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-900 rounded-lg border border-red-900/50 overflow-hidden">
              <div className="px-3 py-2 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-sm font-bold text-red-400">Restringidos</h3>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-900/30 text-red-300 border border-red-800">
                  {sidebarRestringidos.length}
                </span>
              </div>
              <div className="px-3 py-2">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-400">Máximo por apuesta</span>
                  <span className="font-mono text-red-300">{limiteApuestaLabel}</span>
                </div>
                {sidebarRestringidos.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center">Ninguno</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {sidebarRestringidos.map((n) => (
                      <span
                        key={n}
                        className="font-mono text-sm font-bold text-red-400 bg-red-950/50 border border-red-800/60 rounded px-2 py-1"
                      >
                        {n}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Payment Confirmation Modal */}
      <Modal
        isOpen={paymentModal}
        onClose={() => setPaymentModal(false)}
        title="Confirmar Venta"
      >
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600 font-mono">₡{total.toLocaleString()}</p>
            <p className="text-sm text-gray-500 mt-1">{currentItems.length} jugada{currentItems.length !== 1 ? 's' : ''}</p>
          </div>

          {(paymentNotice || paymentError) && (
            <div
              className={`rounded border px-3 py-2 text-sm ${paymentError
                ? 'bg-red-50 border-red-200 text-red-700'
                : 'bg-blue-50 border-blue-200 text-blue-700'
              }`}
            >
              {paymentError ? paymentError : paymentNotice}
            </div>
          )}

          {riesgoAdvertencia && !paymentError && (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {riesgoAdvertencia}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                // Abrir el jalador como acción aparte (no vender directamente)
                setPaymentModal(false);
                setPullTicketCode('');
                setPullTicketError(null);
                setPullTicketModal(true);
                setTimeout(() => pullTicketInputRef.current?.focus(), 50);
              }}
              className="w-full"
            >
              Jalar ticket
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setPaymentModal(false);
                setAnularTicketModal(true);
              }}
              className="w-full text-red-600 border-red-300 hover:bg-red-50"
            >
              Anular ticket
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setPaymentModal(false);
                setReimpresionModal(true);
              }}
              className="w-full text-blue-600 border-blue-300 hover:bg-blue-50"
            >
              Reimpresión
            </Button>
          </div>

          <div className="space-y-2">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Método de pago (F2 para cambiar)</p>
            <div className="grid grid-cols-2 gap-2">
              {(['EFECTIVO', 'SINPE', 'TRANSFERENCIA', ...(canUseCredit ? ['CREDITO'] : [])] as PaymentMethod[]).map(m => (
                <button
                  key={m}
                  onClick={() => {
                    setPaymentMethod(m);
                    if (m === 'CREDITO') setShowClienteSelector(true);
                  }}
                  className={`py-3 text-sm font-bold rounded transition-all ${
                    paymentMethod === m
                      ? 'bg-yellow-500 text-black'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {m === 'EFECTIVO' ? '💵 Efectivo' : m === 'SINPE' ? '📱 SINPE' : m === 'TRANSFERENCIA' ? '🏦 Transfer' : '💳 Crédito'}
                </button>
              ))}
            </div>
          </div>

          {/* Campo de nombre de cliente (no para CREDITO, ahí se toma del cliente seleccionado) */}
          {paymentMethod !== 'CREDITO' && (
            <div className="space-y-1">
              <label className="text-xs text-gray-500 uppercase tracking-wide">Cliente</label>
              <input
                ref={modalClienteNombreInputRef}
                type="text"
                placeholder="Nombre del cliente (opcional)"
                className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:border-yellow-500 focus:outline-none"
              />
            </div>
          )}

          {/* Campo de referencia para SINPE o TRANSFERENCIA */}
          {(paymentMethod === 'SINPE' || paymentMethod === 'TRANSFERENCIA') && (
            <div className="space-y-1">
              <label className="text-xs text-gray-500 uppercase tracking-wide">Referencia *</label>
              <input
                ref={modalReferenciaInputRef}
                type="text"
                placeholder={paymentMethod === 'SINPE' ? 'Últimos 4 dígitos o nombre' : 'Número de referencia'}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:border-yellow-500 focus:outline-none"
              />
            </div>
          )}

          {/* Selector de cliente para CRÉDITO */}
          {paymentMethod === 'CREDITO' && (
            <div className="space-y-2">
              <label className="text-xs text-gray-500 uppercase tracking-wide">Cliente *</label>
              <button
                type="button"
                onClick={() => setShowClienteSelector(true)}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded text-left hover:bg-gray-50 flex items-center gap-2"
              >
                <User size={16} className="text-gray-400" />
                {modalClienteRef.current ? modalClienteRef.current.nombre : 'Seleccionar cliente...'}
              </button>
              {modalClienteRef.current && (() => {
                const saldo = Number(modalClienteRef.current.saldo_pendiente || 0);
                const limite = Number(modalClienteRef.current.limite_credito || 0);
                const disponible = limite > 0 ? Math.max(limite - saldo, 0) : null;
                const excede = disponible !== null && total > disponible;
                return (
                  <div className={`text-xs p-2 rounded ${excede ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                    {limite > 0 ? (
                      <div className="flex justify-between">
                        <span>Saldo: ₡{saldo.toLocaleString()}</span>
                        <span>Límite: ₡{limite.toLocaleString()}</span>
                        <span className={`font-semibold ${excede ? 'text-red-600' : 'text-green-600'}`}>
                          Disp: ₡{disponible!.toLocaleString()}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-500">Sin límite de crédito definido</span>
                    )}
                    {excede && <p className="text-red-600 font-semibold mt-1">⚠ Crédito insuficiente para ₡{total.toLocaleString()}</p>}
                  </div>
                );
              })()}
              {modalClienteRef.current && (
                <input
                  ref={modalReferenciaInputRef}
                  type="text"
                  placeholder="Referencia (opcional)"
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:border-yellow-500 focus:outline-none"
                />
              )}
            </div>
          )}

          {/* Botones de acción */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="secondary"
              onClick={() => setPaymentModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                if (currentItems.length === 0) {
                  setPaymentError('No hay jugadas. Usa “Jalar ticket” o captura una jugada.');
                  return;
                }
                // Enviar por WhatsApp sin imprimir
                const referenciaValue = modalReferenciaInputRef.current?.value?.trim() || '';
                const canConfirm = paymentMethod === 'EFECTIVO' || 
                  ((paymentMethod === 'SINPE' || paymentMethod === 'TRANSFERENCIA') && referenciaValue) ||
                  (paymentMethod === 'CREDITO' && modalClienteRef.current);
                if (!canConfirm) {
                  setPaymentError(paymentMethod === 'CREDITO' ? 'Selecciona un cliente' : 'Ingresa la referencia');
                  return;
                }

                // ⚠️ CAPTURAR VALORES ANTES DE SETPAYMENTMODAL(FALSE)
                const clienteNombreACapturar = modalClienteNombreInputRef.current?.value?.trim() || '';
                const referenciaACapturar = modalReferenciaInputRef.current?.value?.trim() || '';
                pendingClienteNombreRef.current = clienteNombreACapturar;
                pendingReferenciaRef.current = referenciaACapturar;

                setPaymentError(null);
                setPaymentNotice(null);
                setPaymentModal(false);
                setCliente(modalClienteRef.current);
                setReferencia(referenciaACapturar);
                handleConfirm().then(code => {
                  if (code) {
                    const msg = `Ticket: ${code}\nTotal: ₡${total.toLocaleString()}\nMétodo: ${paymentMethod}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                  }
                });
              }}
              className="flex-1 flex items-center justify-center gap-1"
              disabled={currentItems.length === 0}
            >
              <Send size={16} /> WhatsApp
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                if (currentItems.length === 0) {
                  setPaymentError('No hay jugadas. Usa “Jalar ticket” o captura una jugada.');
                  return;
                }
                const referenciaValue = modalReferenciaInputRef.current?.value?.trim() || '';
                const canConfirm = paymentMethod === 'EFECTIVO' || 
                  ((paymentMethod === 'SINPE' || paymentMethod === 'TRANSFERENCIA') && referenciaValue) ||
                  (paymentMethod === 'CREDITO' && modalClienteRef.current);
                if (!canConfirm) {
                  setPaymentError(paymentMethod === 'CREDITO' ? 'Selecciona un cliente' : 'Ingresa la referencia');
                  return;
                }

                setPaymentError(null);
                setPaymentNotice(null);
                setPaymentModal(false);
                setCliente(modalClienteRef.current);
                setReferencia(modalReferenciaInputRef.current?.value?.trim() || '');
                // Capturar valores en refs temporales
                pendingClienteNombreRef.current = modalClienteNombreInputRef.current?.value?.trim() || '';
                pendingReferenciaRef.current = modalReferenciaInputRef.current?.value?.trim() || '';
                handleConfirm();
              }}
              className="flex-1 bg-green-600 hover:bg-green-500 flex items-center justify-center gap-1"
              disabled={currentItems.length === 0 || submitting || isSorteoClosed}
            >
              <Printer size={16} /> Imprimir (*)
            </Button>
          </div>
          
          {/* Acceso rápido a sorteos abiertos */}
          {(() => {
            const tipoMap: Record<string, SorteoType> = {};
            SORTEO_CONFIG.forEach(sc => { tipoMap[tipoIdFromSorteoType(sc.id)] = sc.id; });
            const EXCLUIDOS: SorteoType[] = ['MONAZOS', 'FECHAS', 'PARLEY'];
            const allOpen = backendSorteos.filter(s =>
              s.estado === 'ABIERTO' && !EXCLUIDOS.includes(tipoMap[s.tipo_id])
            );
            if (allOpen.length === 0) return null;
            return (
              <div className="space-y-1">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Sorteos abiertos</p>
                <div className="flex flex-wrap gap-1.5">
                  {allOpen.map((s, i) => {
                    const sorteoType = tipoMap[s.tipo_id];
                    const isActive = sorteoType === activeSorteo && selectedSorteoId[sorteoType] === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => {
                          if (sorteoType) {
                            setActiveSorteo(sorteoType);
                            setSelectedSorteoId(prev => ({ ...prev, [sorteoType]: s.id }));
                          }
                          setPaymentModal(false);
                        }}
                        className={`px-2.5 py-1.5 rounded border text-xs font-bold transition-all ${
                          isActive
                            ? 'bg-yellow-500 text-black border-yellow-600'
                            : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                        }`}
                      >
                        {s.nombre}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <p className="text-xs text-center text-gray-400">Enter o * para imprimir</p>
        </div>
      </Modal>

      {/* Pull Ticket Modal */}
      <Modal
        isOpen={pullTicketModal}
        onClose={() => setPullTicketModal(false)}
        title="Jalar ticket"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">Ingresa el número de ticket para copiar solo los números jugados (sin sorteo ni monto).</p>
          <input
            ref={pullTicketInputRef}
            type="text"
            value={pullTicketCode}
            onChange={e => setPullTicketCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                pullTicketByCode(pullTicketCode);
              }
            }}
            placeholder="Número de ticket"
            className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:border-yellow-500 focus:outline-none font-mono"
            autoComplete="off"
          />
          {pullTicketError && (
            <div className="rounded border bg-red-50 border-red-200 text-red-700 px-3 py-2 text-sm">
              {pullTicketError}
            </div>
          )}

          {/* Últimos 5 tickets para jalar rápido */}
          {(() => {
            const recent = Array.from(ticketHistoryRef.current.entries())
              .map(([code, v]) => ({ code, ...v }))
              .sort((a, b) => b.createdAt - a.createdAt)
              .slice(0, 5);
            if (recent.length === 0) return null;
            return (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Recientes</p>
                <div className="space-y-1">
                  {recent.map(t => (
                    <button
                      key={t.code}
                      onClick={() => {
                        pullTicketByCode(t.code);
                      }}
                      className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-yellow-50 border border-gray-200 hover:border-yellow-400 rounded text-sm transition-colors"
                    >
                      <span className="font-mono font-bold text-gray-900">{t.code}</span>
                      <span className="text-xs text-gray-500">{t.items.length} jugadas</span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setPullTicketModal(false)} className="flex-1">
              Cancelar
            </Button>
            <Button variant="primary" onClick={() => pullTicketByCode(pullTicketCode)} className="flex-1">
              Cargar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cliente Selector Modal */}
      <Modal
        isOpen={showClienteSelector}
        onClose={() => { setShowClienteSelector(false); setClienteSearch(''); }}
        title="Seleccionar Cliente"
      >
        <div className="space-y-3">
          {/* Búsqueda */}
          <input
            type="text"
            value={clienteSearch}
            onChange={e => setClienteSearch(e.target.value)}
            placeholder="Buscar por nombre o teléfono..."
            className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:border-purple-500 focus:outline-none text-sm"
            autoFocus
          />

          {/* Lista de clientes */}
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {(() => {
              const filtered = apiClientes.filter(c => {
                if (!clienteSearch.trim()) return true;
                const q = clienteSearch.toLowerCase();
                return c.nombre.toLowerCase().includes(q) || (c.telefono || '').toLowerCase().includes(q);
              });
              if (filtered.length === 0) {
                return <p className="text-center text-gray-500 py-4 text-sm">No hay clientes disponibles</p>;
              }
              return filtered.map(c => {
                const saldo = Number(c.saldo_pendiente || 0);
                const limite = Number(c.limite_credito || 0);
                const disponible = limite > 0 ? Math.max(limite - saldo, 0) : null;
                const excede = disponible !== null && total > disponible;
                return (
                  <button
                    key={c.id}
                    onClick={() => {
                      modalClienteRef.current = c;
                      setShowClienteSelector(false);
                      setClienteSearch('');
                      if (excede) {
                        setPaymentError(`Crédito insuficiente. Disponible: ₡${disponible!.toLocaleString()}, Total: ₡${total.toLocaleString()}`);
                      }
                    }}
                    className={`
                      w-full p-3 text-left rounded border transition-all
                      ${modalClienteRef.current?.id === c.id
                        ? 'bg-purple-100 border-purple-500'
                        : excede
                          ? 'bg-red-50 border-red-200 hover:bg-red-100'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }
                    `}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{c.nombre}</p>
                        {c.telefono && <p className="text-xs text-gray-500">{c.telefono}</p>}
                      </div>
                      <div className="text-right text-xs">
                        {limite > 0 && (
                          <>
                            <p className="text-gray-500">Límite: ₡{limite.toLocaleString()}</p>
                            <p className="text-gray-500">Saldo: ₡{saldo.toLocaleString()}</p>
                            <p className={`font-semibold ${excede ? 'text-red-600' : 'text-green-600'}`}>
                              Disp: ₡{disponible!.toLocaleString()}
                            </p>
                          </>
                        )}
                        {limite === 0 && (
                          <p className="text-gray-400 italic">Sin límite</p>
                        )}
                      </div>
                    </div>
                    {excede && (
                      <p className="text-xs text-red-500 mt-1">⚠ Crédito insuficiente para esta venta</p>
                    )}
                  </button>
                );
              });
            })()}
          </div>
        </div>
      </Modal>

      {/* Success Modal (fallback si el preview falla) */}
      <Modal
        isOpen={successModal}
        onClose={() => setSuccessModal(false)}
        title=""
      >
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold mb-2">¡Venta Confirmada!</h3>
          <p className="text-gray-500 font-mono text-lg mb-4">{lastTicketCode}</p>
          <Button variant="primary" onClick={() => setSuccessModal(false)}>
            Continuar (ESC)
          </Button>
        </div>
      </Modal>

      {/* Ticket Preview Modal (flujo post-venta) */}
      <TicketPreviewModal
        payload={ticketPreviewPayload}
        onClose={() => setTicketPreviewPayload(null)}
      />

      {/* Anular Ticket Modal */}
      <AnularTicketModal
        isOpen={anularTicketModal}
        onClose={() => setAnularTicketModal(false)}
        sorteoId={currentSorteoId}
        usuarioId={user?.id || 'VENDEDOR-1'}
      />

      {/* Reimpresión Ticket Modal */}
      <ReimpresionTicketModal
        isOpen={reimpresionModal}
        onClose={() => setReimpresionModal(false)}
        onReimprimir={(payload) => setTicketPreviewPayload(payload)}
      />
    </div>
  );
};

export default POSTerminal;
